import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { PlayCircleOutlined, HistoryOutlined, CloseOutlined, DownOutlined, RightOutlined, CheckOutlined } from '@ant-design/icons';
import { Modal, Button, Select, Checkbox, Popconfirm, Input } from 'antd';
import { retranslateSubtitleApi } from '../../service/subtitles';
import RoleDropdown from './RoleDropdown';
import VolumeAdjustmentPopup from './VolumeAdjustmentPopup';
import SpeedAdjustmentPopup from './SpeedAdjustmentPopup';
import { getSubtitleByIdApi, getSubtitlesBySpeakerApi, getChangeToneOptionsApi, changeVoiceApi, deleteSubtitleApi, splitSubtitleApi, mergeSubtitleApi, audioRegenerateApi, updateSubtitleApi, queryByTaskApi, addRemarkApi, adjustVolumeAndSpeedApi, getSubtitleHistoryApi, restoreSubtitleHistoryApi } from '../../service/subtitles';
import { audioManager } from '../../utils/audioManager';
import { getSeriesSpeakersApi, demoteSpeakerSubtitleApi, createSpeakerApi, deleteSpeakerApi } from '../../service/speakers';
import { useAlert } from '../../hooks/useAlert';
import { AlertMessage } from './AlertMessage';
import '../videoEdit/RowItem.scss';
import { VIDEO_CDN_PREFIX } from '../../config/video';
import { processTransAudioUrl, processVideoUrl } from '../../utils/videoUrl';

// 时间戳转换和计算工具函数 - 高精度版本
const parseTimeToSeconds = (timeStr: string): number => {
    // 解析时间格式 "00:00:01,850" 为秒数，使用高精度计算
    const [time, milliseconds] = timeStr.split(',');
    const [hours, minutes, seconds] = time.split(':').map(Number);
    const ms = parseInt(milliseconds);
    
    // 使用整数运算避免浮点数精度问题
    const totalMs = hours * 3600000 + minutes * 60000 + seconds * 1000 + ms;
    return totalMs / 1000;
};

const formatTimeFromSeconds = (seconds: number): string => {
    // 将秒数转换为时间格式 "00:00:01,850"，使用高精度计算
    // 先转换为毫秒进行整数运算，避免浮点数误差
    const totalMs = Math.round(seconds * 1000);
    
    const hours = Math.floor(totalMs / 3600000);
    const minutes = Math.floor((totalMs % 3600000) / 60000);
    const secs = Math.floor((totalMs % 60000) / 1000);
    const ms = totalMs % 1000;
    
    return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')},${ms.toString().padStart(3, '0')}`;
};

// 辅助函数：安全访问字幕数据字段（兼容 GetSubtitleResponse 和 Task.Subtitles 两种格式）
const getSubtitleField = {
    volumeGainDb: (data?: Subtitles.GetSubtitleResponse | Task.Subtitles | null): number | undefined => {
        if (!data) return undefined;
        return 'volume_gain_db' in data ? (data as Subtitles.GetSubtitleResponse).volume_gain_db : 
               'volumeGainDb' in data ? (data as Task.Subtitles).volumeGainDb : undefined;
    },
    taskId: (data?: Subtitles.GetSubtitleResponse | Task.Subtitles | null): number | undefined => {
        if (!data) return undefined;
        return 'task_id' in data ? (data as Subtitles.GetSubtitleResponse).task_id : 
               'taskId' in data ? (data as Task.Subtitles).taskId : undefined;
    },
    postStartTime: (data?: Subtitles.GetSubtitleResponse | Task.Subtitles | null): string | undefined => {
        if (!data) return undefined;
        return 'post_start_time' in data ? (data as Subtitles.GetSubtitleResponse).post_start_time : 
               'postStartTime' in data ? (data as Task.Subtitles).postStartTime : undefined;
    },
    postEndTime: (data?: Subtitles.GetSubtitleResponse | Task.Subtitles | null): string | undefined => {
        if (!data) return undefined;
        return 'post_end_time' in data ? (data as Subtitles.GetSubtitleResponse).post_end_time : 
               'postEndTime' in data ? (data as Task.Subtitles).postEndTime : undefined;
    },
    originStartTime: (data?: Subtitles.GetSubtitleResponse | Task.Subtitles | null): string | undefined => {
        if (!data) return undefined;
        return 'origin_start_time' in data ? (data as Subtitles.GetSubtitleResponse).origin_start_time : 
               'originStartTime' in data ? (data as Task.Subtitles).originStartTime : undefined;
    },
    originEndTime: (data?: Subtitles.GetSubtitleResponse | Task.Subtitles | null): string | undefined => {
        if (!data) return undefined;
        return 'origin_end_time' in data ? (data as Subtitles.GetSubtitleResponse).origin_end_time : 
               'originEndTime' in data ? (data as Task.Subtitles).originEndTime : undefined;
    },
    isEpisodeVoice: (data?: Subtitles.GetSubtitleResponse | Task.Subtitles | null): boolean | undefined => {
        if (!data) return undefined;
        return 'is_episode_voice' in data ? (data as Subtitles.GetSubtitleResponse).is_episode_voice : 
               'isEpisodeVoice' in data ? (data as Task.Subtitles).isEpisodeVoice : undefined;
    },
    transAudio: (data?: Subtitles.GetSubtitleResponse | Task.Subtitles | null): string | undefined => {
        if (!data) return undefined;
        return 'trans_audio' in data ? (data as Subtitles.GetSubtitleResponse).trans_audio : 
               'transAudio' in data ? (data as Task.Subtitles).transAudio : undefined;
    },
    originAudio: (data?: Subtitles.GetSubtitleResponse | Task.Subtitles | null): string | undefined => {
        if (!data) return undefined;
        return 'origin_audio' in data ? (data as Subtitles.GetSubtitleResponse).origin_audio : 
               'originAudio' in data ? (data as Task.Subtitles).originAudio : undefined;
    },
    subtitleId: (data?: Subtitles.GetSubtitleResponse | Task.Subtitles | null): number | undefined => {
        if (!data) return undefined;
        return 'subtitle_id' in data ? (data as Subtitles.GetSubtitleResponse).subtitle_id : 
               'subtitleId' in data ? (data as Task.Subtitles).subtitleId : undefined;
    },
    audioSpeed: (data?: Subtitles.GetSubtitleResponse | Task.Subtitles | null): number | undefined => {
        if (!data) return undefined;
        return 'audio_speed' in data ? (data as Subtitles.GetSubtitleResponse).audio_speed : 
               'audioSpeed' in data ? (data as Task.Subtitles).audioSpeed : undefined;
    },
    speaker: (data?: Subtitles.GetSubtitleResponse | Task.Subtitles | null): Subtitles.Speaker | Task.Speaker | undefined => {
        if (!data) return undefined;
        return 'speakers' in data ? (data as Subtitles.GetSubtitleResponse).speakers : 
               'speaker' in data ? (data as Task.Subtitles).speaker : undefined;
    },
    voiceInfo: (data?: Subtitles.GetSubtitleResponse | Task.Subtitles | null): Subtitles.VoiceInfo | undefined => {
        if (!data) return undefined;
        return 'voice_info' in data ? (data as Subtitles.GetSubtitleResponse).voice_info : undefined;
    },
    originText: (data?: Subtitles.GetSubtitleResponse | Task.Subtitles | null): string | undefined => {
        if (!data) return undefined;
        return 'origin_text' in data ? (data as Subtitles.GetSubtitleResponse).origin_text : 
               'originText' in data ? (data as Task.Subtitles).originText : undefined;
    },
};

const calculateAdjustedTime = (originalTime: string, oldSpeed: number, newSpeed: number): string => {
    // 根据语速变化计算调整后的时间戳，使用高精度计算
    // 公式：新时间 = 原时间 / 旧语速 * 新语速
    const originalSeconds = parseTimeToSeconds(originalTime);
    
    // 使用高精度计算，避免浮点数误差
    const adjustedSeconds = (originalSeconds / oldSpeed) * newSpeed;
    
    // 四舍五入到毫秒精度
    const roundedSeconds = Math.round(adjustedSeconds * 1000) / 1000;
    
    return formatTimeFromSeconds(roundedSeconds);
};

// 添加滚动条样式和动画优化
const scrollbarStyles = `
.add-to-voice-library-scroll::-webkit-scrollbar {
    width: 2px;
}

.add-to-voice-library-scroll::-webkit-scrollbar-track {
    background: transparent;
}

.add-to-voice-library-scroll::-webkit-scrollbar-thumb {
    background: #c1c1c1;
    border-radius: 2px;
}

.add-to-voice-library-scroll::-webkit-scrollbar-thumb:hover {
    background: #a1a1a1;
}

/* 剧集音色库弹窗样式 - 固定高度570px */
.voice-library-modal .ant-modal {
    will-change: transform, opacity;
    backface-visibility: hidden;
    transform: translateZ(0);
}

.voice-library-modal .ant-modal-mask {
    will-change: opacity;
    backface-visibility: hidden;
}

.voice-library-modal .ant-modal-wrap {
    height: auto !important;
}

.voice-library-modal .ant-modal {
    height: auto !important;
    padding-bottom: 0 !important;
}

.voice-library-modal .ant-modal-content {
    will-change: transform;
    backface-visibility: hidden;
    transform: translateZ(0);
    height: 550px !important;
    max-height: 550px !important;
    min-height: 550px !important;
    display: flex !important;
    flex-direction: column !important;
    padding: 0 !important;
    box-sizing: border-box !important;
}

/* 提高优先级，覆盖其他SCSS文件中的样式 */
.voice-library-modal.voice-library-modal .ant-modal-content {
    height: 550px !important;
    max-height: 550px !important;
    min-height: 550px !important;
}

/* 隐藏滚动条但保留滚动功能 */
.voice-library-modal .ant-modal-body {
    scrollbar-width: none !important; /* Firefox */
    -ms-overflow-style: none !important; /* IE 和 Edge */
}

.voice-library-modal .ant-modal-body::-webkit-scrollbar {
    display: none !important; /* Chrome, Safari, Opera */
}

.voice-library-modal .ant-modal-header {
    flex-shrink: 0;
    padding: 16px 24px;
    border-bottom: 1px solid #f0f0f0;
}

.voice-library-modal .ant-modal-body {
    flex: 1;
    overflow-y: auto;
    overflow-x: hidden;
    min-height: 0;
    padding: 20px 24px;
}

/* 剧集音色库弹窗滚动条样式 - 隐藏滚动条但保留滚动功能 */
.voice-library-modal .ant-modal-body {
    scrollbar-width: none !important; /* Firefox */
    -ms-overflow-style: none !important; /* IE 和 Edge */
}

.voice-library-modal .ant-modal-body::-webkit-scrollbar {
    display: none !important; /* Chrome, Safari, Opera */
    width: 0 !important;
    height: 0 !important;
}

/* 优化Modal动画性能 */
.add-to-voice-library-modal .ant-modal {
    will-change: transform, opacity;
    backface-visibility: hidden;
    transform: translateZ(0);
}

.add-to-voice-library-modal .ant-modal-mask {
    will-change: opacity;
    backface-visibility: hidden;
}

.add-to-voice-library-modal .ant-modal-content {
    will-change: transform;
    backface-visibility: hidden;
    transform: translateZ(0);
}
`;

// 注入样式（存在则更新 content，确保高度等修改能生效）
if (typeof document !== 'undefined') {
    const id = 'style[data-add-to-voice-library-scroll]';
    let styleElement = document.head.querySelector(id) as HTMLStyleElement | null;
    if (!styleElement) {
        styleElement = document.createElement('style');
        styleElement.setAttribute('data-add-to-voice-library-scroll', 'true');
        document.head.appendChild(styleElement);
    }
    styleElement.textContent = scrollbarStyles;
}

interface SourceData {
    id: number;
    text: string;
    timeRange: string;
    task_id?: number;
    speaker_id?: number;
    subtitle_id?: number; // 添加subtitle_id字段，用于获取字幕详情
}

interface TargetData {
    id: number;
    text: string;
    timeRange: string;
    subtitle_id?: number; // 添加subtitle_id字段
}

interface RowItemProps {
    sourceData: SourceData;
    targetData: TargetData;
    onSourceDataChange: (newData: SourceData) => void;
    onTargetDataChange: (newData: TargetData) => void;
    roles: Array<{ key: string; label: string }>;
    selectedRole: string;
    onRoleSelect: (roleName: string, rowId: number, speakerId?: string, isSeriesVoiceLibrary?: boolean) => void;
    onNewSpeaker: (rowId: number) => void | Promise<void>;
    editingRoleKey: string | null;
    editingRoleName: string;
    onStartEditRole: (roleKey: string, currentName: string) => void;
    onSaveRoleEdit: (roleKey: string) => Promise<void>;
    onCancelRoleEdit: () => void;
    onEditingRoleNameChange: (name: string) => void;
    onDeleteRole?: (roleKey: string) => void;
    showNewSpeaker?: boolean;
    onSeekToTime?: (timeRange: string) => void;
    highlightedSubtitleId?: number | null;
    // 全局翻译和配音状态
    isGlobalRetranslating?: boolean;
    isGlobalRedubbing?: boolean;
    // 单个台词的处理状态 - 用于控制单个台词的禁用状态
    subtitleProcessingState?: { isRetranslating: boolean; isRedubbing: boolean };
    // 场景4：字幕编辑时的音频控制
    onAudioPause?: () => void;
    onAudioSync?: () => void;
    // 剧集台词标记
    isSeriesSpeaker?: boolean;
    // 台词播放功能
    onPlaySubtitle?: (timeRange: string) => void;
    onPlayTargetSubtitle?: (timeRange: string, subtitleId?: number) => void;
    // 更新videoData中的语速数据
    onUpdateVideoDataSpeed?: (subtitleId: number, audioSpeed: number) => void;
    // 字幕更新状态
    isUpdating?: boolean;
    // 切换轨道回调函数
    onSwitchTrack?: (trackType: 'translated' | 'original') => void;
    updateError?: string;
    // 字幕数据（包含音量等信息）
    subtitleData?: Subtitles.GetSubtitleResponse | Task.Subtitles;
    // 获取当前音量的回调函数
    onGetCurrentVolume?: (subtitleId: number) => number;
    // 更新音量的回调函数
    onUpdateVolume?: (subtitleId: number, volume: number) => void;
    // 获取当前倍速的回调函数
    onGetCurrentSpeed?: (subtitleId: number) => number;
    // 更新倍速的回调函数
    onUpdateSpeed?: (subtitleId: number, speed: number) => void;
    // 剧集说话人角色数据（父组件传入，包含说话人ID）
    seriesSpeakers?: Array<{ id: number; key: string; label: string; description: string }>;
    // 将字幕载入剧集说话人的回调函数
    onPromoteToSeriesSpeaker?: (speakerId: number, subtitleId: number) => Promise<void>;
    // 删除剧集说话人角色后的本地同步（父组件维护的 seriesSpeakers 列表）
    onDeleteSeriesSpeaker?: (speakerId: number) => void;
    // 剧集ID
    seriesId?: number;
    // 语言代码（用于获取指定语言的角色列表）
    langCode?: string;
    // 删除字幕的回调函数
    onDeleteSubtitle?: (subtitleId: number) => void;
    // 新增字幕片段的回调函数
    onAddSubtitle?: (newSubtitle: {
        task_id: number;
        origin_text: string;
        post_start_time: string;
        post_end_time: string;
        origin_start_time: string;
        origin_end_time: string;
        speaker_id: number;
    }, isLocalInsert?: boolean, currentSubtitleId?: number) => void;
    // 字幕拆分更新的回调函数
    onSubtitleUpdate?: (originalSubtitle: Subtitles.GetSubtitleResponse, newSubtitle: Subtitles.GetSubtitleResponse | null) => void;
    // 获取下一个台词信息的回调函数
    onGetNextSubtitle?: (currentId: number) => { startTime: string; endTime: string } | null;
    // 获取下一个字幕ID的回调函数
    onGetNextSubtitleId?: (currentId: number) => number | null;
    // 获取完整时间轴数据的回调函数
    onGetFullTimelineData?: () => any;
    // 留言相关props
    commentText?: string;
    onCommentChange?: (subtitleId: number, comment: string) => void;
    // 生成中状态，禁用所有编辑功能
    isGenerating?: boolean;
    // 刷新字幕列表回调（更换音色后重新获取/video/subtitles/list接口）
    onRefreshSubtitles?: () => Promise<void>;
    // 回译相关props
    backTranslateText?: string; // 回译文本（常显）
    // 是否隐藏目标语言列 & 留言列（例如纯净版校对模式）
    hideTargetAndComment?: boolean;
    // 是否为纯净版模式（用于调整样式，例如源语言左边距、播放按钮常显）
    isPureMode?: boolean;
    // 历史记录面板相关
    isHistoryPanelOpen?: boolean; // 是否打开历史记录面板（由父组件控制）
    onOpenHistoryPanel?: (subtitleId: number) => void; // 打开历史记录面板的回调
    onCloseHistoryPanel?: () => void; // 关闭历史记录面板的回调
}

const RowItem: React.FC<RowItemProps> = ({
    sourceData,
    targetData,
    onSourceDataChange,
    onTargetDataChange,
    roles,
    selectedRole,
    onRoleSelect,
    onNewSpeaker,
    editingRoleKey,
    editingRoleName,
    onStartEditRole,
    onSaveRoleEdit,
    onCancelRoleEdit,
    onEditingRoleNameChange,
    onDeleteRole,
    showNewSpeaker = true,
    onSeekToTime,
    highlightedSubtitleId,
    isGlobalRetranslating = false,
    isGlobalRedubbing = false,
    subtitleProcessingState = { isRetranslating: false, isRedubbing: false },
    // 场景4：字幕编辑时的音频控制
    onAudioPause,
    onAudioSync,
    // 剧集台词标记
    isSeriesSpeaker: propIsSeriesSpeaker = false,
    // 台词播放功能
    onPlaySubtitle,
    onPlayTargetSubtitle,
    // 更新videoData中的语速数据
    onUpdateVideoDataSpeed,
    // 字幕更新状态
    isUpdating = false,
    // 切换轨道回调函数
    onSwitchTrack,
    updateError,
    // 字幕数据
    subtitleData: propSubtitleData,
    // 获取当前音量的回调函数
    onGetCurrentVolume,
    // 更新音量的回调函数
    onUpdateVolume,
    // 获取当前倍速的回调函数
    onGetCurrentSpeed,
    // 更新倍速的回调函数
    onUpdateSpeed,
    // 将字幕载入剧集说话人的回调函数
    onPromoteToSeriesSpeaker,
    // 父组件传入的剧集说话人角色列表（已经按 langCode 过滤，包含id）
    seriesSpeakers: parentSeriesSpeakers = [],
    // 删除剧集说话人角色后的父组件同步
    onDeleteSeriesSpeaker,
    // 剧集ID
    seriesId,
    // 语言代码（用于获取指定语言的角色列表）
    langCode,
    // 删除字幕的回调函数
    onDeleteSubtitle,
    // 新增字幕片段的回调函数
    onAddSubtitle,
    // 字幕拆分更新的回调函数
    onSubtitleUpdate,
    // 获取下一个台词信息的回调函数
    onGetNextSubtitle,
    // 获取下一个字幕ID的回调函数
    onGetNextSubtitleId,
    // 获取完整时间轴数据的回调函数
    onGetFullTimelineData,
    // 留言相关props
    commentText = '',
    onCommentChange,
    // 生成中状态，禁用所有编辑功能
    isGenerating = false,
    // 刷新字幕列表回调
    onRefreshSubtitles,
    // 回译相关props
    backTranslateText = '',
    hideTargetAndComment = false,
    isPureMode = false,
    // 历史记录面板相关
    isHistoryPanelOpen = false,
    onOpenHistoryPanel,
    onCloseHistoryPanel
}) => {
    // 从 URL 参数获取 seriesId 和 langCode
    const [searchParams] = useSearchParams();
    const urlSeriesId = searchParams.get('seriesId');
    const urlLangCode = searchParams.get('langCode');
    
    // 🍎 从 localStorage 读取角色列表并备份（进入页面时执行一次）
    useEffect(() => {
        const finalSeriesId = urlSeriesId ? parseInt(urlSeriesId) : seriesId;
        const finalLangCode = urlLangCode || langCode;
        
        if (finalSeriesId && finalLangCode) {
            const storageKey = `seriesSpeakers_${finalSeriesId}_${finalLangCode}`;
            try {
                const savedListStr = localStorage.getItem(storageKey);
                if (savedListStr) {
                    const savedList = JSON.parse(savedListStr);
                    if (Array.isArray(savedList)) {
                        setLocalSeriesSpeakers(savedList);
                        console.log('🍎 从本地加载角色列表（进入页面）:', savedList);
                    }
                } else if (parentSeriesSpeakers && parentSeriesSpeakers.length > 0) {
                    // 如果本地没有，但父组件有数据，则使用父组件的数据并备份到本地
                    setLocalSeriesSpeakers(parentSeriesSpeakers);
                    localStorage.setItem(storageKey, JSON.stringify(parentSeriesSpeakers));
                    console.log('🍎 使用父组件数据并备份到本地:', parentSeriesSpeakers);
                } else if (parentSeriesSpeakers && parentSeriesSpeakers.length === 0) {
                    // 如果父组件传入空数组，也同步清空本地状态
                    setLocalSeriesSpeakers([]);
                    localStorage.setItem(storageKey, JSON.stringify([]));
                }
            } catch (error) {
                console.error('🍎 从本地读取角色列表失败:', error);
                // 读取失败时，使用父组件传入的数据
                if (parentSeriesSpeakers && parentSeriesSpeakers.length > 0) {
                    setLocalSeriesSpeakers(parentSeriesSpeakers);
                }
            }
        }
    }, [parentSeriesSpeakers, urlSeriesId, urlLangCode, seriesId, langCode]);
    
    // 调试信息：打印处理状态
    useEffect(() => {
        if (subtitleProcessingState.isRetranslating || subtitleProcessingState.isRedubbing) {
            console.log(`台词 ${sourceData.id} 处理状态:`, subtitleProcessingState);
        }
    }, [subtitleProcessingState, sourceData.id]);
    
    // 每行独立的状态管理
    const [editingField, setEditingField] = useState<string | null>(null);
    const [inputHeight, setInputHeight] = useState<number | null>(null);
    const [showActionBar, setShowActionBar] = useState(false); // 点击目标语言输入框时显示
    const [showSourceActionBar, setShowSourceActionBar] = useState(false); // 点击源语言输入框时显示
    const [showAdjustDropdown, setShowAdjustDropdown] = useState(false);
    const [showCustomCard, setShowCustomCard] = useState(false);
    const [customText, setCustomText] = useState('');
    const [isRetranslating, setIsRetranslating] = useState(false);
    const retranslateRequestRef = useRef<Promise<void> | null>(null);
    const [isSeriesSpeaker, setIsSeriesSpeaker] = useState(false);
    
    // 留言编辑状态
    const [isEditingComment, setIsEditingComment] = useState(false);
    const [localCommentText, setLocalCommentText] = useState(commentText);

    const [showChangeVoiceModal, setShowChangeVoiceModal] = useState(false); // 更换音色模态框
    const [showVoiceLibraryModal, setShowVoiceLibraryModal] = useState(false); // 剧集音色库模态框
    const [showAddToVoiceLibraryModal, setShowAddToVoiceLibraryModal] = useState(false); // 添加至剧集音色库模态框
    const [showSegmentList, setShowSegmentList] = useState(false); // 显示其他片段列表
    const [showVolumeAdjustment, setShowVolumeAdjustment] = useState(false); // 音量调节弹出框
    const [volumeAdjustmentPosition, setVolumeAdjustmentPosition] = useState({ top: 0, left: 0 }); // 音量调节弹出框位置
    const [showSpeedAdjustment, setShowSpeedAdjustment] = useState(false); // 倍速调节弹出框
    const [speedAdjustmentPosition, setSpeedAdjustmentPosition] = useState({ top: 0, left: 0 }); // 倍速调节弹出框位置
    const [speedResetTrigger, setSpeedResetTrigger] = useState(0); // 语速重置触发器
    const [subtitleData, setSubtitleData] = useState<Subtitles.GetSubtitleResponse | Task.Subtitles | null>(null); // 字幕数据
    const [originalSubtitleData, setOriginalSubtitleData] = useState<Subtitles.GetSubtitleResponse | Task.Subtitles | null>(null); // 原始字幕数据（用于保持弹窗顶部显示不变）
    const [loadingSubtitle, setLoadingSubtitle] = useState(false); // 加载字幕数据状态
    const [loadingVolumeData, setLoadingVolumeData] = useState(false); // 加载音量数据状态
    const [currentVolume, setCurrentVolume] = useState<number | null>(null); // 当前音量状态（独立于subtitleData）
    const [isVolumeManuallySet, setIsVolumeManuallySet] = useState(false); // 标记音量是否被手动设置
    const [speakerSubtitles, setSpeakerSubtitles] = useState<Subtitles.GetSubtitlesBySpeakerResponse>([]); // 说话人字幕列表

    // 同步 propSubtitleData 到本地 state，确保重新配音后的数据能正确显示
    useEffect(() => {
        if (propSubtitleData) {
            setSubtitleData(propSubtitleData);
        }
    }, [propSubtitleData]);
    
    // 初始化音量状态（只有在没有被手动设置时才自动更新）
    useEffect(() => {
        if (!isVolumeManuallySet) {
            const volume = getSubtitleField.volumeGainDb(propSubtitleData) ?? getSubtitleField.volumeGainDb(subtitleData);
            if (volume !== undefined) {
                setCurrentVolume(volume);
            }
        }
    }, [propSubtitleData, subtitleData, isVolumeManuallySet]);
    const [loadingSpeakerSubtitles, setLoadingSpeakerSubtitles] = useState(false); // 加载说话人字幕列表状态
    const [isPlayingAudio, setIsPlayingAudio] = useState(false); // 主语音条播放状态
    const [playingSegmentId, setPlayingSegmentId] = useState<number | null>(null); // 正在播放的片段ID
    const [changingVoice, setChangingVoice] = useState(false); // 更换音色状态
    const [selectedSegment, setSelectedSegment] = useState<any>(null); // 选中的片段
    const audioRef = useRef<HTMLAudioElement | null>(null); // 音频引用
    const [expandedRoles, setExpandedRoles] = useState<{ [key: string]: boolean }>({}); // 展开的角色状态
    const [selectedRoleForVoiceLibrary, setSelectedRoleForVoiceLibrary] = useState<string>(''); // 选择的角色
    // 本地剧集说话人数据（包含片段信息），用于"剧集音色库"主弹窗。
    // "添加至剧集音色库"弹窗只需要角色名称，使用本地状态 localSeriesSpeakers 以便同步新增的角色
    const [seriesSpeakers, setSeriesSpeakers] = useState<Speakers.Speaker[]>([]); // 剧集说话人数据（含片段）
    // 本地角色列表状态，用于"添加至剧集音色库"弹窗，支持新增角色后立即显示
    const [localSeriesSpeakers, setLocalSeriesSpeakers] = useState<Array<{ key: string; label: string; id?: number; description?: string }>>([]);
    const [loadingSeriesSpeakers, setLoadingSeriesSpeakers] = useState(false); // 加载剧集说话人状态
    // 新建角色相关状态
    const [isCreatingRole, setIsCreatingRole] = useState(false);
    const [deletingRoleId, setDeletingRoleId] = useState<number | null>(null);
    const [newRoleName, setNewRoleName] = useState('');
    const [playingVoiceClipId, setPlayingVoiceClipId] = useState<string | null>(null); // 正在播放的音色片段ID
    const [selectedVoiceClip, setSelectedVoiceClip] = useState<Speakers.Subtitle | null>(null); // 选中的音色片段（剧集音色库弹窗中）
    const [applyToAllSegments, setApplyToAllSegments] = useState(false); // 是否应用到所有片段
    const [isDeleting, setIsDeleting] = useState(false); // 删除状态
    const [isSplitting, setIsSplitting] = useState(false);
    const [isMerging, setIsMerging] = useState(false); // 拆分状态
    const [isRedubbing, setIsRedubbing] = useState(false); // 重新配音状态
    const [isAddingToVoiceLibrary, setIsAddingToVoiceLibrary] = useState(false); // 添加至音色库loading状态
    const [cursorPosition, setCursorPosition] = useState<number>(0); // 光标位置
    const [lastClickPosition, setLastClickPosition] = useState<number>(0); // 最后一次点击的光标位置
    const [lastClickSubtitleId, setLastClickSubtitleId] = useState<number>(0); // 最后一次点击的字幕ID
    
    // 使用AlertMessage
    const { alertConfig, showAlert, showError, showSuccess, hideAlert } = useAlert();

    // 每行独立的内容状态
    const [sourceText, setSourceText] = useState(sourceData.text);
    const [targetText, setTargetText] = useState(targetData.text);
    
    // 同步 targetData 和 sourceData prop 的变化到本地状态
    useEffect(() => {
        // 只有当不在编辑状态时才同步，避免覆盖用户正在编辑的内容
        if (editingField !== 'target' && targetData.text !== targetText) {
            setTargetText(targetData.text);
        }
    }, [targetData.text, editingField]);
    
    useEffect(() => {
        // 只有当不在编辑状态时才同步，避免覆盖用户正在编辑的内容
        if (editingField !== 'source' && sourceData.text !== sourceText) {
            setSourceText(sourceData.text);
        }
    }, [sourceData.text, editingField]);
    
    // 检测是否为新增的字幕（临时ID为负数）
    const isNewSubtitle = sourceData.id < 0;
    
    // 临时字幕禁用状态
    const isTemporarySubtitle = isNewSubtitle;
    
    // 防止多次提交的状态
    const [isSubmitting, setIsSubmitting] = useState(false);
    // 创建字幕的加载状态
    const [isCreating, setIsCreating] = useState(false);
    
    // 当字幕不再是临时字幕时，重置提交状态
    useEffect(() => {
        if (!isNewSubtitle) {
            setIsSubmitting(false);
            setIsCreating(false);
            // 创建完成后退出编辑模式
            if (editingField === 'source') {
                setEditingField(null);
            }
        }
    }, [isNewSubtitle]); // 移除editingField依赖，避免循环触发
    
    // 处理临时字幕的点击事件
    const handleTemporarySubtitleClick = () => {
        if (isTemporarySubtitle) {
            showError('请输入原文台词回车后操作');
        }
    };

    // 时间编辑状态
    const [editingTimeField, setEditingTimeField] = useState<string | null>(null);
    const [startTime, setStartTime] = useState(targetData.timeRange.split(' → ')[0]);
    const [endTime, setEndTime] = useState(targetData.timeRange.split(' → ')[1]);
    const [timeError, setTimeError] = useState<string | null>(null);

    const sourceInputRef = useRef<HTMLTextAreaElement>(null);
    const targetInputRef = useRef<HTMLTextAreaElement>(null);
    const startTimeRef = useRef<HTMLInputElement>(null);
    const endTimeRef = useRef<HTMLInputElement>(null);
    const sourceDialogueTextRef = useRef<HTMLDivElement>(null);
    const targetDialogueTextRef = useRef<HTMLDivElement>(null);
    const cursorPositionRef = useRef<number>(0); // 保存光标位置（DOM中的实际字符位置）

    const MAX_EDIT_HEIGHT_PX = 75;

    // 台词历史记录状态
    interface SubtitleHistoryItem {
        id: number;
        postText: string;
        transAudio: string;
        transAudioTs: string;
        audioSpeed: number;
        volumeGainDb: number;
        postStartTime?: string; // 开始时间
        postEndTime?: string; // 结束时间
        selected: boolean; // true-使用中（正在应用）
    }

    // 计算时长：从开始时间和结束时间计算
    const calculateDuration = (startTime?: string, endTime?: string): string => {
        if (!startTime || !endTime) {
            return '00:00:000';
        }

        try {
            const startSeconds = parseTimeToSeconds(startTime);
            const endSeconds = parseTimeToSeconds(endTime);
            const durationSeconds = endSeconds - startSeconds;

            if (durationSeconds < 0) {
                return '00:00:000';
            }

            // 格式化为 MM:SS:MMM 格式（分钟:秒:毫秒，毫秒三位数）
            const totalMs = Math.round(durationSeconds * 1000);
            const minutes = Math.floor(totalMs / 60000);
            const seconds = Math.floor((totalMs % 60000) / 1000);
            const milliseconds = totalMs % 1000;

            return `${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}:${milliseconds.toString().padStart(3, '0')}`;
        } catch (error) {
            console.error('计算时长失败:', error);
            return '00:00:000';
        }
    };

    // 历史记录面板状态 - 使用父组件传入的状态
    const [historyLoading, setHistoryLoading] = useState(false);
    const [historyList, setHistoryList] = useState<SubtitleHistoryItem[]>([]);
    const [activeHistoryId, setActiveHistoryId] = useState<number | null>(null);
    const [isRestoring, setIsRestoring] = useState(false);
    const historyAudioRef = useRef<HTMLAudioElement | null>(null);
    const historyPanelRef = useRef<HTMLDivElement | null>(null);
    const historyButtonRef = useRef<HTMLElement | null>(null);
    const historyPrefetchRef = useRef<{ subtitleId: number; fetched: boolean }>({ subtitleId: -1, fetched: false });
    const shouldShowHistoryIcon = historyList.length >= 2;

    // subtitleId 变化时，重置历史缓存（避免跨台词复用）
    useEffect(() => {
        historyPrefetchRef.current = { subtitleId: targetData.id, fetched: false };
        setHistoryList([]);
        setActiveHistoryId(null);
    }, [targetData.id]);

    const fetchSubtitleHistory = useCallback(async (options?: { silent?: boolean }) => {
        const silent = options?.silent === true;
        try {
            const response = await getSubtitleHistoryApi(targetData.id);
            if (response.code === 0 && Array.isArray(response.data)) {
                // 如果历史记录中没有开始时间和结束时间，从当前字幕数据中获取
                const currentPostStartTime = getSubtitleField.postStartTime(propSubtitleData);
                const currentPostEndTime = getSubtitleField.postEndTime(propSubtitleData);

                const historyListWithTimes = response.data.map(item => ({
                    ...item,
                    // 如果历史记录中没有时间，使用当前字幕的时间
                    postStartTime: item.postStartTime || currentPostStartTime,
                    postEndTime: item.postEndTime || currentPostEndTime
                }));

                setHistoryList(historyListWithTimes);

                // 自动选中当前正在使用的记录：只使用 selected===true
                const matchedItem = historyListWithTimes.find(item => item.selected);
                setActiveHistoryId(matchedItem ? matchedItem.id : null);

                historyPrefetchRef.current = { subtitleId: targetData.id, fetched: true };
                return historyListWithTimes;
            }

            if (!silent) {
                showError(response.msg || '获取历史记录失败');
            }
            return [];
        } catch (error) {
            if (!silent) {
                console.error('获取台词历史记录失败:', error);
                const err: any = error;
                const errorMsg = err?.response?.data?.msg ||
                    err?.response?.msg ||
                    err?.msg ||
                    err?.message ||
                    '获取历史记录失败，请重试';
                showError(errorMsg);
            }
            return [];
        }
    }, [propSubtitleData, showError, targetData.id]);

    const ensureHistoryPrefetched = useCallback(async () => {
        if (isTemporarySubtitle || isGenerating) return;
        if (historyPrefetchRef.current.fetched && historyPrefetchRef.current.subtitleId === targetData.id) {
            return;
        }
        await fetchSubtitleHistory({ silent: true });
    }, [fetchSubtitleHistory, isGenerating, isTemporarySubtitle, targetData.id]);

    const handleHistoryIconClick = useCallback(async () => {
        if (isGenerating) {
            return;
        }
        if (isTemporarySubtitle) {
            handleTemporarySubtitleClick();
            return;
        }

        const willOpen = !isHistoryPanelOpen;

        if (!willOpen) {
            // 关闭历史音频播放
            if (historyAudioRef.current) {
                historyAudioRef.current.pause();
                historyAudioRef.current.currentTime = 0;
            }
            setActiveHistoryId(null);
            if (onCloseHistoryPanel) {
                onCloseHistoryPanel();
            }
            return;
        }

        if (onOpenHistoryPanel) {
            onOpenHistoryPanel(targetData.id);
        }

        setHistoryLoading(true);
        try {
            const list = await fetchSubtitleHistory({ silent: false });
            // 若历史不足两条，则直接不打开（并给用户明确反馈）
            if (!Array.isArray(list) || list.length < 2) {
                if (onCloseHistoryPanel) {
                    onCloseHistoryPanel();
                }
            }
        } finally {
            setHistoryLoading(false);
        }
    }, [
        fetchSubtitleHistory,
        handleTemporarySubtitleClick,
        isGenerating,
        isHistoryPanelOpen,
        isTemporarySubtitle,
        onCloseHistoryPanel,
        onOpenHistoryPanel,
        targetData.id
    ]);

    // 计算内容所需高度
    const getContentHeight = (el: HTMLElement) => {
        const minHeight = 20;
        const originalHeight = el.style.height;
        el.style.height = 'auto';
        const contentHeight = Math.max(el.scrollHeight, minHeight);
        el.style.height = originalHeight;
        return contentHeight;
    };

    // 调整输入框高度 - 同步两个输入框的高度
    const adjustTextareaHeight = (el: HTMLTextAreaElement | null) => {
        if (!el) return;

        // 计算当前输入框的内容高度
        const currentHeight = getContentHeight(el);

        // 计算另一个输入框的内容高度
        const otherInput = el === sourceInputRef.current ? targetInputRef.current : sourceInputRef.current;
        const otherHeight = otherInput ? getContentHeight(otherInput) : 20;

        // 取两者中的较大值，但不超过最大高度
        const maxHeight = Math.max(currentHeight, otherHeight);
        const newHeight = Math.min(maxHeight, MAX_EDIT_HEIGHT_PX);

        // 只有当新高度与当前高度不同时才更新，避免不必要的重新渲染
        if (inputHeight !== newHeight) {
            setInputHeight(newHeight);
        }
    };

    // 开始编辑
    const startEditing = (field: string) => {
        setEditingField(field);

        // 先显示操作条，提供即时反馈
        if (field === 'source') {
            setShowSourceActionBar(true);
            // 点击源语言输入框时，只显示源语言操作条
        } else if (field === 'target') {
            setShowActionBar(true);
            // 点击目标语言输入框时，只显示目标语言操作条
        }

        // 异步执行其他操作，避免阻塞UI
        setTimeout(() => {
            // 跳转到对应时间（根据编辑字段使用对应的时间戳）
            if (onSeekToTime) {
                if (field === 'source') {
                    // 编辑原文台词时，使用原文台词的时间戳
                    onSeekToTime(sourceData.timeRange);
                } else if (field === 'target') {
                    // 编辑翻译台词时，使用翻译台词的时间戳
                    onSeekToTime(targetData.timeRange);
                }
            }

            // 场景4：字幕编辑时暂停音频
            if (onAudioPause) {
                onAudioPause();
            }
            // 注意：光标位置设置在 input 的 onFocus 事件中处理
        }, 0);
    };

    // 临时字幕的保存编辑（向后端新增台词）
    const saveTemporarySubtitle = (field: string, value: string) => {
        if (field === 'source') {
            setSourceText(value);
            // 临时字幕不调用onSourceDataChange，因为还没有在数据库中，不需要修改
            
            // 临时字幕：第一次回车触发API请求，向后端新增台词
            if (value.trim() && onAddSubtitle && !isSubmitting && !isCreating) {
                // 防止多次提交
                setIsSubmitting(true);
                setIsCreating(true);
                
                // 立即关闭黑色悬浮操作条
                setShowSourceActionBar(false);
                
                // 使用点击新增片段时保存的时间戳数据，加上用户输入的文字
                // 关键点：新增字幕时，origin 与 post 时间戳可能不同，必须分别取 sourceData/targetData 的 timeRange
                const [originStartTime, originEndTime] = (sourceData.timeRange || '').split(' → ');
                const [rawPostStartTime, rawPostEndTime] = ((targetData?.timeRange || sourceData.timeRange) || '').split(' → ');

                const postStartTime = isPureMode ? originStartTime : rawPostStartTime;
                const postEndTime = isPureMode ? originEndTime : rawPostEndTime;
                
                // 构建新增字幕的参数，使用保存的时间戳数据
                const newSubtitleParams = {
                    task_id: 0, // 这个会由父组件设置
                    origin_text: value.trim(), // 用户输入的文字
                    post_start_time: postStartTime, // 目标台词时间轴时间戳（编辑页可能与原文不同）
                    post_end_time: postEndTime,
                    origin_start_time: originStartTime, // 原文时间轴时间戳
                    origin_end_time: originEndTime,
                    speaker_id: 0 // 这个会由父组件从当前字幕中获取
                };
                
                // 调用接口请求（isLocalInsert为false），向后端新增台词
                onAddSubtitle(newSubtitleParams, false, sourceData.id);
                
                // 注意：创建状态会在useEffect中重置，当isNewSubtitle变为false时
                // 在创建期间保持编辑状态，不退出编辑模式
                return; // 提前返回，不执行setEditingField(null)
            }
        } else if (field === 'target') {
            setTargetText(value);
            // 临时字幕不调用onTargetDataChange，因为还没有在数据库中，不需要修改
        }
        setEditingField(null);
    };

    // 普通字幕的保存编辑（调用修改字幕接口）
    const saveNormalSubtitle = (field: string, value: string) => {
        if (field === 'source') {
            setSourceText(value);
            onSourceDataChange({ ...sourceData, text: value });
            
            // 普通字幕：调用修改字幕接口
            if (value.trim()) {
                // 这里可以添加调用修改字幕接口的逻辑
                // 目前只是更新本地状态，实际接口调用可能在父组件中处理
                console.log('普通字幕修改:', { id: sourceData.id, field, value });
            }
        } else if (field === 'target') {
            setTargetText(value);
            onTargetDataChange({ ...targetData, text: value });
            
            // 普通字幕：调用修改字幕接口
            if (value.trim()) {
                // 这里可以添加调用修改字幕接口的逻辑
                // 目前只是更新本地状态，实际接口调用可能在父组件中处理
                console.log('普通字幕修改:', { id: targetData.id, field, value });
            }
        }
        setEditingField(null);
    };

    // 统一的保存编辑函数
    const saveEdit = (field: string, value: string) => {
        if (isNewSubtitle) {
            // 临时字幕：向后端新增台词
            saveTemporarySubtitle(field, value);
        } else {
            // 普通字幕：调用修改字幕接口
            saveNormalSubtitle(field, value);
        }
    };

    // 时间格式验证
    const validateTimeFormat = (time: string): { isValid: boolean; error: string | null } => {
        if (!time || time.trim() === '') {
            return { isValid: false, error: '时间不能为空' };
        }

        const timeRegex = /^\d{2}:\d{2}:\d{2},\d{3}$/;
        if (!timeRegex.test(time)) {
            return { isValid: false, error: '格式错误，应为 HH:MM:SS,mmm' };
        }

        const parts = time.split(/[:,]/);
        const hours = parseInt(parts[0]);
        const minutes = parseInt(parts[1]);
        const seconds = parseInt(parts[2]);
        const milliseconds = parseInt(parts[3]);

        if (hours < 0 || hours > 23) {
            return { isValid: false, error: '小时应在 00-23 范围内' };
        }
        if (minutes < 0 || minutes > 59) {
            return { isValid: false, error: '分钟应在 00-59 范围内' };
        }
        if (seconds < 0 || seconds > 59) {
            return { isValid: false, error: '秒数应在 00-59 范围内' };
        }
        if (milliseconds < 0 || milliseconds > 999) {
            return { isValid: false, error: '毫秒应在 000-999 范围内' };
        }

        return { isValid: true, error: null };
    };

    // 开始编辑时间
    const startTimeEditing = (field: string) => {
        setEditingTimeField(field);
    };

    // 保存时间编辑
    const saveTimeEdit = (field: string, value: string) => {
        const validation = validateTimeFormat(value);

        if (validation.isValid) {
            if (field === 'start') {
                setStartTime(value);
            } else if (field === 'end') {
                setEndTime(value);
            }
            setTimeError(null);
            setEditingTimeField(null);

            // 更新时间范围 - 同时更新源语言和目标语言的时间
            const newTimeRange = field === 'start'
                ? `${value} → ${endTime}`
                : `${startTime} → ${value}`;
            onSourceDataChange({ ...sourceData, timeRange: newTimeRange });
            onTargetDataChange({ ...targetData, timeRange: newTimeRange });
        } else {
            setTimeError(validation.error);
        }
    };

    // 操作条功能处理
    const handleActionBarAction = async (action: string, event?: React.MouseEvent) => {
        console.log(`执行操作: ${action}`);
        if (action === 'adjust-translation') {
            setShowAdjustDropdown(!showAdjustDropdown);
        } else if (action === 'change-voice') {
            // 输出点击台词的subtitleId（优先使用subtitle_id，如果没有则使用id）
            const subtitleId = sourceData.subtitle_id || sourceData.id;
            console.log('🎤 点击更换音色，点击台词的subtitleId:', subtitleId, 'sourceData:', { id: sourceData.id, subtitle_id: sourceData.subtitle_id });
            // 先打开弹窗
            setShowChangeVoiceModal(true);
            // 每次点击更换音色时，重新获取最新的字幕数据
            handleGetSubtitleData();
        } else if (action === 'adjust-volume') {
            // 先显示弹窗，再异步加载数据
            const buttonElement = event?.currentTarget;
            
            // 立即显示音量调节弹出框
            if (buttonElement) {
                const rect = buttonElement.getBoundingClientRect();
                setVolumeAdjustmentPosition({
                    top: rect.bottom + 10,
                    left: rect.left
                });
            }
            setShowVolumeAdjustment(true);
            
            // 优先使用传入的字幕数据，如果没有则异步加载
            if (propSubtitleData) {
                // 使用传入的字幕数据，无需加载
                setSubtitleData(propSubtitleData);
                console.log('使用传入的字幕数据，音量:', getSubtitleField.volumeGainDb(propSubtitleData));
            } else if (!subtitleData) {
                // 如果没有传入数据且本地也没有，则异步加载
                setLoadingVolumeData(true);
                const loadSubtitleData = async () => {
                    try {
                        const subtitleId = targetData.id;
                        const subtitleResponse = await getSubtitleByIdApi(subtitleId);
                        setSubtitleData(subtitleResponse);
                        console.log('音量数据加载完成:', getSubtitleField.volumeGainDb(subtitleResponse));
                    } catch (error) {
                        console.error('获取字幕数据失败:', error);
                        // 不显示错误提示，因为弹窗已经显示了
                    } finally {
                        setLoadingVolumeData(false);
                    }
                };
                loadSubtitleData();
            }
        } else if (action === 'adjust-speed') {
            // 先显示弹窗，再异步加载数据
            const buttonElement = event?.currentTarget;
            
            // 立即显示倍速调节弹出框
            if (buttonElement) {
                const rect = buttonElement.getBoundingClientRect();
                setSpeedAdjustmentPosition({
                    top: rect.bottom + 10,
                    left: rect.left
                });
            }
            setShowSpeedAdjustment(true);
            
            // 异步加载字幕数据（如果还没有的话）
            if (!subtitleData) {
                const loadSubtitleData = async () => {
                    try {
                        const subtitleId = targetData.id;
                        const subtitleResponse = await getSubtitleByIdApi(subtitleId);
                        setSubtitleData(subtitleResponse);
                    } catch (error) {
                        console.error('获取字幕数据失败:', error);
                        // 不显示错误提示，因为弹窗已经显示了
                    }
                };
                loadSubtitleData();
            }
        } else if (action === 're-dub') {
            // 处理重新配音
            await handleAudioRegenerate();
        } else {
            // 执行操作后不自动关闭操作条，让用户可以通过点击外部关闭
            // 操作条会通过点击外部区域关闭
        }
    };

    // 处理音量调节确认
    const handleVolumeAdjustmentConfirm = async (volume: number, applyToAll: boolean) => {
        try {
            console.log(`音量调节确认: volume=${volume}, applyToAll=${applyToAll}`);
            
            // 立即更新音量状态，不依赖API响应
            setCurrentVolume(volume);
            setIsVolumeManuallySet(true); // 标记为手动设置
            
            // 通知父组件更新音量映射表
            if (onUpdateVolume) {
                onUpdateVolume(targetData.id, volume);
            }
            
            console.log('立即更新音量状态:', volume);
            
            // 如果当前正在播放音频，立即更新音量设置
            if (isPlayingAudio) {
                const audioId = `target_audio_${targetData.id}`;
                if (audioManager.isPlaying(audioId)) {
                    audioManager.setVolumeGain(audioId, volume);
                    console.log('立即更新播放中的音频音量:', volume);
                }
            }
            
            // 确保有字幕数据
            if (!subtitleData) {
                console.error('字幕数据不存在，无法调节音量');
                showAlert('error', '字幕数据不存在，请重试');
                return;
            }
            
            if (applyToAll) {
                // 如果勾选了"应用到全部音频"，使用统一接口，传入applyToAll: true
                console.log('开始批量调节音量，应用到全部音频');
                
                // 获取当前audioSpeed，用于批量调用
                const currentAudioSpeed = getSubtitleField.audioSpeed(subtitleData) ?? 1.0;
                
                // 获取时间戳
                const postStartTime = getSubtitleField.postStartTime(subtitleData) || getSubtitleField.originStartTime(subtitleData) || '';
                const postEndTime = getSubtitleField.postEndTime(subtitleData) || getSubtitleField.originEndTime(subtitleData) || '';
                
                // 调用统一接口，传入applyToAll: true
                const response = await adjustVolumeAndSpeedApi({
                    subtitleId: targetData.id,  // 使用当前字幕的id
                    volumeGainDb: volume,
                    audioSpeed: currentAudioSpeed,  // 保持当前倍速不变
                    applyToAll: true,  // 批量调节时传入true
                    postStartTime: postStartTime,
                    postEndTime: postEndTime
                });
                
                console.log('批量音量调节API响应:', response);
                
                // API调用成功，同步更新本地数据
                if (response.code === 0 && response.data && response.data.length > 0) {
                    console.log('批量音量调节API调用成功，共处理', response.data.length, '个字幕');
                    
                    // 同步更新volumeGainDb到本地数据
                    const updatedSubtitle = response.data[0];
                    if (updatedSubtitle.volumeGainDb !== undefined) {
                        setCurrentVolume(updatedSubtitle.volumeGainDb);
                    }
                    
                    // 通知父组件更新本地数据
                    if (onRefreshSubtitles) {
                        await onRefreshSubtitles();
                    }
                }
                
                // 显示成功消息
                const successCount = response.code === 0 && response.data ? response.data.length : 0;
                showAlert('success', `音量调节成功！已应用到全部 ${successCount} 个音频`);
                
            } else {
                // 只调节当前字幕
                // 获取当前的audioSpeed，如果没有则默认为1.0
                const currentAudioSpeed = getSubtitleField.audioSpeed(subtitleData) ?? 1.0;
                
                // 获取时间戳
                const postStartTime = getSubtitleField.postStartTime(subtitleData) || getSubtitleField.originStartTime(subtitleData) || '';
                const postEndTime = getSubtitleField.postEndTime(subtitleData) || getSubtitleField.originEndTime(subtitleData) || '';
                
                // 调用统一接口
                const response = await adjustVolumeAndSpeedApi({
                    subtitleId: targetData.id,  // 使用targetData.id作为字幕的id
                    volumeGainDb: volume,
                    audioSpeed: currentAudioSpeed,
                    applyToAll: applyToAll,  // 根据用户操作传值
                    postStartTime: postStartTime,
                    postEndTime: postEndTime
                });
                
                console.log('音量调节API响应:', response);
                
                // API调用成功，同步更新本地数据
                if (response.code === 0 && response.data && response.data.length > 0) {
                    const updatedSubtitle = response.data[0];
                    console.log('单个音量调节API调用成功，更新本地数据');
                    
                    // 更新subtitleData以保持数据一致性
                    // 将Task.Subtitles格式转换为GetSubtitleResponse格式（如果需要）
                    // 或者直接使用Task.Subtitles格式更新
                    if (updatedSubtitle.volumeGainDb !== undefined) {
                        setCurrentVolume(updatedSubtitle.volumeGainDb);
                    }
                    if (updatedSubtitle.audioSpeed !== undefined && onUpdateVideoDataSpeed) {
                        onUpdateVideoDataSpeed(targetData.id, updatedSubtitle.audioSpeed);
                    }
                    
                    // 通知父组件更新本地数据（如果需要）
                    if (onRefreshSubtitles) {
                        await onRefreshSubtitles();
                    }
                }
                
                // 显示成功消息
                showAlert('success', '音量调节成功');
            }
            
        } catch (error) {
            console.error('音量调节失败:', error);
            
            // API调用失败，回滚音量状态
            const originalVolume = getSubtitleField.volumeGainDb(subtitleData) ?? 1.0;
            setCurrentVolume(originalVolume);
            setIsVolumeManuallySet(false); // 重置手动设置标志
            console.log('API调用失败，回滚音量状态到:', originalVolume);
            
            // 如果正在播放音频，也回滚音频音量
            if (isPlayingAudio) {
                const audioId = `target_audio_${targetData.id}`;
                if (audioManager.isPlaying(audioId)) {
                    audioManager.setVolumeGain(audioId, originalVolume);
                    console.log('回滚播放中的音频音量到:', originalVolume);
                }
            }
            
            showAlert('error', '音量调节失败，请重试');
        }
    };

    // 处理倍速调节确认
    const handleSpeedAdjustmentConfirm = async (speed: number) => {
        try {
            console.log(`倍速调节确认: speed=${speed}`);
            
            // 先获取字幕数据，使用targetData.id作为字幕ID
            const subtitleId = targetData.id;
            console.log('获取字幕数据，字幕ID:', subtitleId);
            
            const subtitleResponse = await getSubtitleByIdApi(subtitleId);
            setSubtitleData(subtitleResponse);
            
            console.log('字幕数据:', subtitleResponse);
            
            // 获取原始语速和时间戳
            const oldSpeed = getSubtitleField.audioSpeed(subtitleResponse) ?? 1.0;
            const originalStartTime = getSubtitleField.postStartTime(subtitleResponse) || getSubtitleField.originStartTime(subtitleResponse) || '';
            const originalEndTime = getSubtitleField.postEndTime(subtitleResponse) || getSubtitleField.originEndTime(subtitleResponse) || '';
            
            console.log('原始语速和时间戳:', {
                oldSpeed,
                originalStartTime,
                originalEndTime
            });
            
            // 根据需求：开始时间戳使用原始时间戳，结束时间戳需要根据语速变化计算
            // 重要：始终基于原始时间戳计算，避免累积误差
            const adjustedStartTime = originalStartTime; // 开始时间戳保持不变
            
            // 计算原始时长（基于原始时间戳）
            const originalDuration = parseTimeToSeconds(originalEndTime) - parseTimeToSeconds(originalStartTime);
            
            // 根据语速变化计算新的时长
            const newDuration = (originalDuration / oldSpeed) * speed;
            
            // 计算新的结束时间戳（开始时间 + 新时长）
            const newEndTimeSeconds = parseTimeToSeconds(originalStartTime) + newDuration;
            const adjustedEndTime = formatTimeFromSeconds(Math.round(newEndTimeSeconds * 1000) / 1000);
            
            // 检查时间戳冲突：判断修改倍速后的结束时间戳与下一句的开始时间戳是否冲突
            if (onGetNextSubtitle) {
                const nextSubtitleInfo = onGetNextSubtitle(targetData.id);
                if (nextSubtitleInfo) {
                    const adjustedEndTimeSeconds = parseTimeToSeconds(adjustedEndTime);
                    const nextStartTimeSeconds = parseTimeToSeconds(nextSubtitleInfo.startTime);
                    
                    // 如果调整后的结束时间大于或等于下一个台词的开始时间，则冲突
                    if (adjustedEndTimeSeconds >= nextStartTimeSeconds) {
                        console.log('时间戳冲突检测:', {
                            adjustedEndTime,
                            nextStartTime: nextSubtitleInfo.startTime,
                            adjustedEndTimeSeconds,
                            nextStartTimeSeconds,
                            conflict: true
                        });
                        
                        showAlert('error', '不可与下一段台词时间重叠');
                        
                        // 重置语速调节弹窗的状态
                        setSpeedResetTrigger(prev => prev + 1);
                        
                        return; // 停止执行，不进行语速调节
                    }
                    
                    console.log('时间戳冲突检测通过:', {
                        adjustedEndTime,
                        nextStartTime: nextSubtitleInfo.startTime,
                        adjustedEndTimeSeconds,
                        nextStartTimeSeconds,
                        conflict: false
                    });
                }
            }
            
            console.log('调整后的时间戳:', {
                adjustedStartTime,
                adjustedEndTime,
                originalDuration: `${originalDuration.toFixed(3)}s`,
                newDuration: `${newDuration.toFixed(3)}s`,
                speedRatio: `${(speed / oldSpeed).toFixed(3)}`,
                note: '开始时间戳保持原始值，结束时间戳根据语速变化计算，基于原始时长避免累积误差'
            });
            
            // 获取当前的volumeGainDb，如果没有则默认为1.0
            const currentVolumeGainDb = getSubtitleField.volumeGainDb(subtitleResponse) ?? 1.0;
            
            // 获取时间戳（使用调整后的时间戳）
            const postStartTime = adjustedStartTime || getSubtitleField.postStartTime(subtitleResponse) || getSubtitleField.originStartTime(subtitleResponse) || '';
            const postEndTime = adjustedEndTime || getSubtitleField.postEndTime(subtitleResponse) || getSubtitleField.originEndTime(subtitleResponse) || '';
            
            // 调用统一接口，applyToAll固定为false（调节倍速时）
            const response = await adjustVolumeAndSpeedApi({
                subtitleId: subtitleId,  // 使用targetData.id作为字幕的id
                volumeGainDb: currentVolumeGainDb,  // 保持当前音量不变
                audioSpeed: speed,  // 新的倍速值
                applyToAll: false,  // 调节倍速时固定为false
                postStartTime: postStartTime,
                postEndTime: postEndTime
            });
            
            console.log('语速调节API响应:', response);
            
            // 更新本地数据
            if (response.code === 0 && response.data && response.data.length > 0) {
                const updatedSubtitle = response.data[0];
                console.log('语速调节成功，更新本地数据:', updatedSubtitle);
                
                // 同步更新volumeGainDb和audioSpeed到本地数据
                if (updatedSubtitle.volumeGainDb !== undefined) {
                    setCurrentVolume(updatedSubtitle.volumeGainDb);
                }
                if (updatedSubtitle.audioSpeed !== undefined && onUpdateVideoDataSpeed) {
                    onUpdateVideoDataSpeed(targetData.id, updatedSubtitle.audioSpeed);
                }
                
                // 同步更新targetData的时间戳
                const newTimeRange = `${adjustedStartTime} → ${adjustedEndTime}`;
                onTargetDataChange({
                    ...targetData,
                    timeRange: newTimeRange
                });
                
                // 更新本地时间状态
                setStartTime(adjustedStartTime);
                
                // 通知父组件更新本地数据（如果需要）
                if (onRefreshSubtitles) {
                    await onRefreshSubtitles();
                }
                setEndTime(adjustedEndTime);
                
                // 更新videoData中的语速数据
                if (onUpdateVideoDataSpeed) {
                    onUpdateVideoDataSpeed(subtitleId, speed);
                }
                
                console.log('同步时间戳更新:', {
                    oldTimeRange: targetData.timeRange,
                    newTimeRange: newTimeRange,
                    adjustedStartTime,
                    adjustedEndTime,
                    audioSpeed: speed
                });
                
                // 如果当前正在播放音频，实时更新语速设置
                if (isPlayingAudio) {
                    console.log('实时更新正在播放音频的语速设置');
                    const audioId = `target_audio_${subtitleId}`;
                    
                    // 使用 AudioManager 实时更新语速
                    if (audioManager.isPlaying(audioId)) {
                        audioManager.setPlaybackRate(audioId, speed);
                        console.log(`🔊 实时更新音频语速为: ${speed}x`);
                    }
                }
                
                // 更新倍速映射表
                if (onUpdateSpeed) {
                    onUpdateSpeed(subtitleId, speed);
                }
                
                // 显示成功消息
                showAlert('success', '语速调节成功');
            }
            
        } catch (error) {
            console.error('语速调节失败:', error);
            showAlert('error', '语速调节失败，请重试');
        }
    };

    // 处理重新配音
    const handleAudioRegenerate = async () => {
        try {
            console.log('开始重新配音，字幕ID:', sourceData.id);
            
            // 检查是否有未保存的编辑，如果有，先保存到后端
            const hasUnsavedSource = editingField === 'source' || (sourceText !== sourceData.text);
            const hasUnsavedTarget = editingField === 'target' || (targetText !== targetData.text);
            
            if (hasUnsavedSource || hasUnsavedTarget) {
                console.log('检测到未保存的编辑，先保存文本到后端:', {
                    hasUnsavedSource,
                    hasUnsavedTarget,
                    sourceText,
                    sourceDataText: sourceData.text,
                    targetText,
                    targetDataText: targetData.text
                });
                
                // 先调用本地保存函数，更新父组件状态
                if (hasUnsavedSource && sourceText.trim()) {
                    saveEdit('source', sourceText);
                }
                if (hasUnsavedTarget && targetText.trim()) {
                    saveEdit('target', targetText);
                }
                
                // 解析时间范围字符串
                const parseTimeRange = (timeRange: string) => {
                    if (!timeRange) return ['', ''];
                    const parts = timeRange.split(' → ');
                    return parts.length === 2 ? parts : ['', ''];
                };
                
                // 合并更新：同时保存源文本和目标文本
                try {
                    // 优先从 subtitleData 获取时间字段，如果没有则从 timeRange 解析
                    let postStartTime = getSubtitleField.postStartTime(subtitleData) || '';
                    let postEndTime = getSubtitleField.postEndTime(subtitleData) || '';
                    let originStartTime = getSubtitleField.originStartTime(subtitleData) || '';
                    let originEndTime = getSubtitleField.originEndTime(subtitleData) || '';
                    
                    // 如果 subtitleData 中没有时间字段，从 timeRange 解析
                    if (!postStartTime || !postEndTime) {
                        const [parsedPostStart, parsedPostEnd] = parseTimeRange(targetData.timeRange || '');
                        postStartTime = parsedPostStart || postStartTime;
                        postEndTime = parsedPostEnd || postEndTime;
                    }
                    if (!originStartTime || !originEndTime) {
                        const [parsedOriginStart, parsedOriginEnd] = parseTimeRange(sourceData.timeRange || '');
                        originStartTime = parsedOriginStart || originStartTime;
                        originEndTime = parsedOriginEnd || originEndTime;
                    }
                    
                    // 调用新的更新字幕接口（只支持更新originText）
                    const originText = hasUnsavedSource ? sourceText : (sourceData.text || '');
                    const response = await updateSubtitleApi({
                        id: sourceData.id,
                        originText: originText
                    });
                    
                    console.log('✅ 更新字幕API响应:', response);
                    
                    // 如果响应成功，可以处理响应数据
                    if (response.code === 0 && response.data) {
                        console.log('✅ 字幕更新成功:', response.data);
                        
                        // 修改源台词后，清空对应的目标台词
                        if (onTargetDataChange) {
                            onTargetDataChange({
                                ...targetData,
                                text: ''  // 清空目标台词
                            });
                        }
                    }
                    console.log('文本已保存到后端，重新获取最新数据以确保同步');
                    
                    // 重新从后端获取最新数据，确认文本已真正保存
                    // 使用轮询方式，最多等待3秒
                    let retryCount = 0;
                    const maxRetries = 10;
                    const retryInterval = 300; // 每次等待300ms
                    let dataSynced = false;
                    
                    while (retryCount < maxRetries && !dataSynced) {
                        try {
                            await new Promise(resolve => setTimeout(resolve, retryInterval));
                            const latestSubtitleData = await getSubtitleByIdApi(sourceData.id);
                            
                            // 检查文本是否已真正同步
                            const originTextMatch = !hasUnsavedSource || (latestSubtitleData.origin_text.trim() === sourceText.trim());
                            const postTextMatch = !hasUnsavedTarget || (latestSubtitleData.post_text.trim() === targetText.trim());
                            
                            if (originTextMatch && postTextMatch) {
                                console.log('✅ 确认数据已同步，文本已更新:', {
                                    origin_text: latestSubtitleData.origin_text,
                                    post_text: latestSubtitleData.post_text,
                                    retryCount: retryCount + 1,
                                    totalWaitTime: (retryCount + 1) * retryInterval + 'ms'
                                });
                                // 更新本地subtitleData为最新数据
                                setSubtitleData(latestSubtitleData);
                                dataSynced = true;
                            } else {
                                retryCount++;
                                console.log(`⏳ 数据尚未同步，重试 ${retryCount}/${maxRetries}:`, {
                                    latestOriginText: latestSubtitleData.origin_text,
                                    expectedOriginText: hasUnsavedSource ? sourceText : sourceData.text,
                                    latestPostText: latestSubtitleData.post_text,
                                    expectedPostText: hasUnsavedTarget ? targetText : targetData.text,
                                    originTextMatch,
                                    postTextMatch
                                });
                            }
                        } catch (fetchError) {
                            retryCount++;
                            console.warn(`⚠️ 重新获取数据失败 (${retryCount}/${maxRetries}):`, fetchError);
                            // 如果获取失败，等待后继续重试
                            if (retryCount < maxRetries) {
                                await new Promise(resolve => setTimeout(resolve, retryInterval));
                            }
                        }
                    }
                    
                    if (!dataSynced) {
                        console.warn('⚠️ 数据同步确认失败，但继续执行重新配音（可能使用稍旧的数据）');
                        showAlert('warning', '文本可能尚未完全同步，重新配音可能使用旧文本');
                    }
                } catch (error) {
                    console.error('保存文本失败:', error);
                    showAlert('error', '保存文本失败，无法重新配音');
                    return;
                }
            }
            
            // 设置加载状态
            setIsRedubbing(true);
            
            // 清理音频播放状态，防止状态不一致
            const audioId = `target_audio_${targetData.id}`;
            if (audioManager.isPlaying(audioId)) {
                audioManager.stopSound(audioId);
                audioManager.destroySound(audioId);
            }
            setIsPlayingAudio(false);
            setPlayingSegmentId(null);
            
            // 显示加载状态
            showAlert('info', '正在重新配音，请稍候...');
            
            // 调用重新配音API
            const response = await audioRegenerateApi(sourceData.id);
            
            console.log('重新配音API响应:', response);
            
            if (response) {
                // 更新本地数据（包含最新音频等信息）
                setSubtitleData(response);
                
                // 同步API返回的时间戳到台词显示
                const newPostStartTime = getSubtitleField.postStartTime(response);
                const newPostEndTime = getSubtitleField.postEndTime(response);
                const newOriginStartTime = getSubtitleField.originStartTime(response);
                const newOriginEndTime = getSubtitleField.originEndTime(response);
                
                // 更新源语言台词（原文）
                const newSourceTimeRange = newOriginStartTime && newOriginEndTime
                    ? `${newOriginStartTime} → ${newOriginEndTime}`
                    : sourceData.timeRange;
                
                onSourceDataChange({
                    ...sourceData,
                    text: getSubtitleField.originText(response) || '',
                    timeRange: newSourceTimeRange
                });
                
                // 更新目标语言台词（翻译）
                const newTargetTimeRange = newPostStartTime && newPostEndTime
                    ? `${newPostStartTime} → ${newPostEndTime}`
                    : targetData.timeRange;
                
                onTargetDataChange({
                    ...targetData,
                    text: response.post_text,
                    timeRange: newTargetTimeRange
                });
                
                // 通知父组件更新字幕数据（主要是音频相关字段）
                if (onSubtitleUpdate) {
                    console.log('调用onSubtitleUpdate回调函数，更新重新配音后的数据');
                    // 标记操作类型为重新配音
                    (response as any).operationType = 'redub';
                    onSubtitleUpdate(response, null);
                } else {
                    console.warn('onSubtitleUpdate回调函数不存在');
                }
                
                // 显示成功消息
                showAlert('success', '重新配音成功');

                // 重新配音会产生新的音频历史记录：立即静默刷新 historyList，确保图标与面板状态同步（无需刷新页面）
                try {
                    await fetchSubtitleHistory({ silent: true });
                } catch (e) {
                    // 静默失败不影响主流程
                    console.warn('静默刷新历史记录失败:', e);
                }
            }
            
        } catch (error) {
            console.error('重新配音失败:', error);
            showAlert('error', '重新配音失败，请重试');
        } finally {
            // 重置加载状态
            setIsRedubbing(false);
        }
    };

    // 解析时间字符串为秒数
    const parseTimeToSeconds = (timeStr: string): number => {
        const [time, milliseconds] = timeStr.split(',');
        const [hours, minutes, seconds] = time.split(':').map(Number);
        return hours * 3600 + minutes * 60 + seconds + (milliseconds ? parseInt(milliseconds) / 1000 : 0);
    };

    // 将秒数格式化为时间字符串（使用高精度计算，避免浮点数误差）
    const formatSecondsToTime = (seconds: number): string => {
        // 先转换为毫秒进行整数运算，避免浮点数误差
        const totalMs = Math.round(seconds * 1000);
        
        const hours = Math.floor(totalMs / 3600000);
        const minutes = Math.floor((totalMs % 3600000) / 60000);
        const secs = Math.floor((totalMs % 60000) / 1000);
        const ms = totalMs % 1000;
        
        return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')},${ms.toString().padStart(3, '0')}`;
    };

    // 处理新增片段
    const handleAddSegment = () => {
        console.log('开始新增片段，当前字幕ID:', sourceData.id);
        
        // 获取当前台词的时间戳（原台词和目标台词分别获取）
        const currentOriginTimeRange = sourceData.timeRange.split(' → ');
        const currentTargetTimeRange = targetData.timeRange.split(' → ');
        const currentOriginEndTime = currentOriginTimeRange[1];
        const currentTargetEndTime = currentTargetTimeRange[1];
        
        // 获取下一个台词的时间信息
        let nextSubtitleInfo = null;
        if (onGetNextSubtitle) {
            nextSubtitleInfo = onGetNextSubtitle(sourceData.id);
        }
        
        // 声明新片段的时间戳变量（原台词和目标台词分别计算）
        let originStartTime: string;
        let originEndTime: string;
        let postStartTime: string;
        let postEndTime: string;
        
        // 检查是否有下一个台词
        if (nextSubtitleInfo) {
            // 原台词时间戳计算（基于原台词时间轴）
            const currentOriginEndSeconds = parseTimeToSeconds(currentOriginEndTime);
            const nextOriginStartSeconds = parseTimeToSeconds(
                (nextSubtitleInfo as any).originStartTime || nextSubtitleInfo.startTime
            );
            
            // 计算原台词空白时间
            const originGapSeconds = nextOriginStartSeconds - currentOriginEndSeconds;
            
            // 检查原台词是否有空白时间段
            if (originGapSeconds <= 0) {
                showError(`当前台词后无空白时间段`);
                return;
            }
            
            // 原台词新增片段时间戳：开始时间 = 上一句结束时间 + 1ms，结束时间 = 下一句开始时间 - 1ms
            const originStartSeconds = currentOriginEndSeconds + 0.001;
            const originEndSeconds = nextOriginStartSeconds - 0.001;
            originStartTime = formatSecondsToTime(originStartSeconds);
            originEndTime = formatSecondsToTime(originEndSeconds);
            
            // 调试信息：输出原台词时间戳计算
            console.log('原台词新增片段时间戳计算:', {
                currentOriginEndTime,
                currentOriginEndSeconds,
                nextOriginStartTime: (nextSubtitleInfo as any).originStartTime || nextSubtitleInfo.startTime,
                nextOriginStartSeconds,
                originStartTime,
                originEndTime,
                '开始时间(ms)': (originStartSeconds * 1000).toFixed(2),
                '结束时间(ms)': (originEndSeconds * 1000).toFixed(2)
            });
            
            // 目标台词时间戳计算（基于目标台词时间轴，仅编辑页需要）
            if (!isPureMode) {
                const currentTargetEndSeconds = parseTimeToSeconds(currentTargetEndTime);
                const nextTargetStartSeconds = parseTimeToSeconds(
                    (nextSubtitleInfo as any).postStartTime || nextSubtitleInfo.startTime
                );
                
                // 计算目标台词空白时间
                const targetGapSeconds = nextTargetStartSeconds - currentTargetEndSeconds;
                
                // 检查目标台词是否有空白时间段
                if (targetGapSeconds <= 0) {
                    showError(`当前台词后无空白时间段`);
                    return;
                }
                
                // 目标台词新增片段时间戳：开始时间 = 上一句结束时间 + 1ms，结束时间 = 下一句开始时间 - 1ms
                const postStartSeconds = currentTargetEndSeconds + 0.001;
                const postEndSeconds = nextTargetStartSeconds - 0.001;
                postStartTime = formatSecondsToTime(postStartSeconds);
                postEndTime = formatSecondsToTime(postEndSeconds);
                
                // 调试信息：输出目标台词时间戳计算
                console.log('目标台词新增片段时间戳计算:', {
                    currentTargetEndTime,
                    currentTargetEndSeconds,
                    nextTargetStartTime: (nextSubtitleInfo as any).postStartTime || nextSubtitleInfo.startTime,
                    nextTargetStartSeconds,
                    postStartTime,
                    postEndTime,
                    '开始时间(ms)': (postStartSeconds * 1000).toFixed(2),
                    '结束时间(ms)': (postEndSeconds * 1000).toFixed(2)
                });
            } else {
                // 校对页：目标台词时间戳使用原台词时间戳
                postStartTime = originStartTime;
                postEndTime = originEndTime;
            }
        } else {
            // 如果没有下一个台词，允许在末尾添加
            // 原台词时间戳
            const currentOriginEndSeconds = parseTimeToSeconds(currentOriginEndTime);
            const originStartSeconds = currentOriginEndSeconds + 0.001;
            const originEndSeconds = originStartSeconds + 1; // 增加1秒
            originStartTime = formatSecondsToTime(originStartSeconds);
            originEndTime = formatSecondsToTime(originEndSeconds);
            
            // 目标台词时间戳（仅编辑页需要）
            if (!isPureMode) {
                const currentTargetEndSeconds = parseTimeToSeconds(currentTargetEndTime);
                const postStartSeconds = currentTargetEndSeconds + 0.001;
                const postEndSeconds = postStartSeconds + 1; // 增加1秒
                postStartTime = formatSecondsToTime(postStartSeconds);
                postEndTime = formatSecondsToTime(postEndSeconds);
            } else {
                // 校对页：目标台词时间戳使用原台词时间戳
                postStartTime = originStartTime;
                postEndTime = originEndTime;
            }
        }
        
        // 从当前字幕的speakers中获取speaker_id
        // 这里需要从父组件获取当前字幕的speaker_id
        // 暂时使用0，父组件会从当前字幕中获取正确的speaker_id
        const speakerId = 0;
        
        // 构建新增字幕的参数（暂存到本地，不立即请求接口）
        const newSubtitleParams = {
            task_id: 0, // 这个会由父组件设置
            origin_text: '', // 空文本，等待用户输入
            // 原台词和目标台词时间戳分别计算，可能不同
            origin_start_time: originStartTime,
            origin_end_time: originEndTime,
            // 校对页不传递目标台词时间戳，编辑页传递目标台词时间戳
            post_start_time: isPureMode ? originStartTime : postStartTime,
            post_end_time: isPureMode ? originEndTime : postEndTime,
            speaker_id: speakerId
        };

        // 调用父组件的回调函数，直接添加到本地数据
        if (onAddSubtitle) {
            onAddSubtitle(newSubtitleParams, true, sourceData.id); // true表示本地插入，传递当前字幕ID
        }
        
        // 关闭操作条
        setShowSourceActionBar(false);
    };


    // 判断字符是否为标点符号
    const isPunctuation = (char: string): boolean => {
        // 中文标点符号
        const chinesePunctuation = /[，。！？；：""''（）【】《》、]/;
        // 英文标点符号
        const englishPunctuation = /[,.!?;:"'()\[\]<>\/\\]/;
        // 其他常见标点符号
        const otherPunctuation = /[~`@#$%^&*+=|{}_\-]/;
        
        return chinesePunctuation.test(char) || englishPunctuation.test(char) || otherPunctuation.test(char);
    };

    // 计算排除空格后的字符位置（标点符号也计入长度）
    const calculatePositionWithoutPunctuationAndSpaces = (text: string): number => {
        let position = 0;
        for (let i = 0; i < text.length; i++) {
            const char = text[i];
            // 仅跳过空白字符（空格、制表符、换行），标点符号也参与计数
            if (char !== ' ' && char !== '\t' && char !== '\n') {
                position++;
            }
        }
        return position;
    };

    // 处理文本点击，更新光标位置
    const handleTextClick = (event: React.MouseEvent<HTMLDivElement> | React.KeyboardEvent<HTMLDivElement> | { currentTarget: HTMLDivElement; target: HTMLDivElement }) => {
        const element = event.currentTarget;
        const selection = window.getSelection();
        
        if (!selection || selection.rangeCount === 0) {
            return;
        }
        
        const range = selection.getRangeAt(0);
        
        // 检查光标是否在当前元素内
        if (!element.contains(range.startContainer)) {
            return;
        }
        
        // 获取原始文本
        const originalText = sourceText || sourceData.text || '';
        if (!originalText) {
            console.warn('无法获取原始文本，无法计算光标位置');
            return;
        }
        
        // 使用更准确的方法计算光标位置
        // 方法：创建range从元素开始到光标位置，获取文本长度
        const getCaretPosition = (container: Node, range: Range): number => {
            // 创建一个新的range，从容器开始到光标位置
            const preCaretRange = range.cloneRange();
            preCaretRange.selectNodeContents(container);
            preCaretRange.setEnd(range.startContainer, range.startOffset);
            
            // 获取光标前的文本内容（纯文本，去除所有格式）
            const textBeforeCursor = preCaretRange.toString();
            
            // 同时获取容器的 textContent 作为验证
            const containerText = container.textContent || '';
            
            // 确保计算的位置不超过容器文本长度
            let position = textBeforeCursor.length;
            position = Math.min(position, containerText.length);
            
            return position;
        };
        
        // 获取光标在元素中的字符位置
        const domTextPosition = getCaretPosition(element, range);
        
        // 获取元素的实际文本内容用于验证
        const elementTextContent = element.textContent || '';
        
        // DOM文本位置直接对应原始文本位置（因为显示的文本就是原始文本）
        // 确保位置不超过原始文本长度和DOM文本长度
        let originalPosition = Math.min(domTextPosition, originalText.length, elementTextContent.length);
        
        // 计算排除标点符号和空格后的位置
        const positionWithoutPunctuationAndSpaces = calculatePositionWithoutPunctuationAndSpaces(
            originalText.substring(0, originalPosition)
        );
        
        
        // 保存两个位置：
        // 1. lastClickPosition: 排除标点符号后的位置，用于拆分API
        // 2. cursorPosition: DOM中的实际字符位置，用于设置input光标
        // 使用 ref 立即保存，确保在进入编辑状态时能立即获取到
        cursorPositionRef.current = originalPosition;
        setLastClickPosition(positionWithoutPunctuationAndSpaces);
        setCursorPosition(originalPosition); // 也保存到state，用于UI显示
        setLastClickSubtitleId(sourceData.id);
    };

    // 在编辑状态下更新光标位置（input内）
    const updateCursorPositionForInput = useCallback((cursorPos: number) => {
        if (isSplitting || isMerging) {
            return;
        }

        // 获取当前文本
        const currentText = sourceText || sourceData.text || '';
        if (!currentText || cursorPos > currentText.length) {
            return;
        }

        // 计算排除标点符号和空格后的位置
        const positionWithoutPunctuationAndSpaces = calculatePositionWithoutPunctuationAndSpaces(
            currentText.substring(0, cursorPos)
        );

        
        // 保存位置
        cursorPositionRef.current = cursorPos;
        setLastClickPosition(positionWithoutPunctuationAndSpaces);
        setCursorPosition(cursorPos);
        setLastClickSubtitleId(sourceData.id);
    }, [sourceText, sourceData.text, sourceData.id, isSplitting, isMerging]);

    // 监听全局 selectionchange 事件，捕获所有光标位置变化（包括鼠标点击和键盘移动）
    useEffect(() => {
        let debounceTimer: NodeJS.Timeout | null = null;

        const handleSelectionChange = () => {
            // 防抖：延迟执行，避免频繁触发
            if (debounceTimer) {
                clearTimeout(debounceTimer);
            }

            debounceTimer = setTimeout(() => {
                // 检查当前 selection 是否在当前台词元素内
                const selection = window.getSelection();
                if (!selection || selection.rangeCount === 0) {
                    return;
                }

                const range = selection.getRangeAt(0);
                const element = sourceDialogueTextRef.current;
                
                // 如果元素不存在，或者selection不在元素内，不处理
                if (!element || !element.contains(range.startContainer)) {
                    return;
                }

                // 如果在处理中（拆分、合并），不更新位置
                if (isSplitting || isMerging) {
                    return;
                }

                // 创建合成事件来调用 handleTextClick
                const syntheticEvent = {
                    currentTarget: element,
                    target: element
                };

                handleTextClick(syntheticEvent);
            }, 100); // 防抖延迟100ms
        };

        // 添加全局监听
        document.addEventListener('selectionchange', handleSelectionChange);

        // 清理函数
        return () => {
            if (debounceTimer) {
                clearTimeout(debounceTimer);
            }
            document.removeEventListener('selectionchange', handleSelectionChange);
        };
    }, [isSplitting, isMerging]);

    // 处理拆分台词
    const handleSplitSubtitle = async () => {
        if (isSplitting) return;
        
        try {
            setIsSplitting(true);
            
            // 拆分字幕之前：输出时间轴所有数据的字段和值
            console.log('=== 点击拆分按钮 - 拆分字幕之前 - 时间轴所有数据 ===');
            if (onGetFullTimelineData) {
                onGetFullTimelineData();
            }
            console.log('=== 点击拆分按钮 - 拆分字幕之前 - 数据输出结束 ===');
            
            
            // 如果当前字幕ID与点击时的字幕ID不一致，覆盖成当前字幕的ID
            if (lastClickSubtitleId !== 0 && lastClickSubtitleId !== sourceData.id) {
                console.log('检测到字幕ID不一致，使用当前字幕ID并重置点击位置');
                setLastClickSubtitleId(sourceData.id);
                setLastClickPosition(0);
                showError('请先点击要拆分的字幕文本位置');
                return;
            }
            
            // 验证是否已经点击过文本
            if (lastClickPosition === 0) {
                showError('请先点击要拆分的字幕文本位置');
                return;
            }
            
            // 调用拆分API
            // lastClickPosition 已经是排除标点符号后的位置（从0开始计数）
            // 如果光标在第五个和第六个中间，lastClickPosition 应该是5
            // 后端需要的是拆分位置，应该直接使用 lastClickPosition，不需要+1
            const apiPosition = lastClickPosition;
            
            const response = await splitSubtitleApi({
                subtitleId: sourceData.id,
                position: apiPosition
            });
            
            // 检查响应是否为数组且包含两个字幕
            if (Array.isArray(response) && response.length === 2) {
                console.log('拆分成功，准备更新页面显示');
                console.log('原始字幕:', response[0]);
                console.log('新字幕:', response[1]);
                
                // 拆分成功，更新页面显示
                if (onSubtitleUpdate) {
                    console.log('调用onSubtitleUpdate回调函数');
                    // 传递原始字幕ID，以便在父组件中正确找到要更新的字幕
                    const originalSubtitleWithId = {
                        ...response[0],
                        originalSubtitleId: sourceData.id // 添加原始字幕ID
                    };
                    onSubtitleUpdate(originalSubtitleWithId, response[1]);
                } else {
                    console.warn('onSubtitleUpdate回调函数不存在');
                }
                
                // 关闭操作条
                setShowSourceActionBar(false);
                
                showAlert('success', '拆分成功');
            } else {
                console.error('响应格式错误:', response);
                showError('拆分失败：响应格式错误');
            }
        } catch (error) {
            console.error('拆分台词失败:', error);
            showError('拆分失败，请重试');
        } finally {
            setIsSplitting(false);
            // 重置点击位置，为下次拆分做准备
            setLastClickPosition(0);
        }
    };

    // 处理合并台词
    const handleMergeSubtitle = async () => {
        if (isMerging) return;
        
        try {
            setIsMerging(true);
            
            // 获取下一个字幕的ID（用于后续处理，但不再传给API）
            let nextSubtitleId = null;
            if (onGetNextSubtitleId) {
                nextSubtitleId = onGetNextSubtitleId(sourceData.id);
            }
            
            // 检查是否有下一个字幕
            if (!nextSubtitleId) {
                showError('没有下一个字幕可以合并');
                return;
            }
            
            // 调用合并API（只传当前字幕ID，后端会自动与下一个字幕合并）
            const response = await mergeSubtitleApi({
                subtitleId: sourceData.id
            });
            
            // 检查响应数据是否有效
            if (!response || typeof response !== 'object') {
                throw new Error('合并API返回的数据格式不正确');
            }
            
            // 检查必要的字段是否存在
            if (!response.id || !response.origin_text) {
                throw new Error('合并API返回的数据缺少必要字段');
            }
            
            // 合并成功，更新页面显示
            if (onSubtitleUpdate) {
                // 合并操作需要特殊处理，因为返回的是合并后的字幕，需要删除下一个字幕
                // 我们需要传递当前字幕的ID信息，让handleSubtitleUpdate能够找到正确的字幕
                const mergeData = {
                    ...response,
                    currentSubtitleId: sourceData.id, // 添加当前字幕ID
                    nextSubtitleId: nextSubtitleId    // 添加下一个字幕ID
                };
                onSubtitleUpdate(mergeData, null);
            }
            
            // 关闭操作条
            setShowSourceActionBar(false);
            
            showSuccess('字幕合并成功');
            
        } catch (error: any) {
            console.error('合并台词失败:', error);
            // 提取错误信息，优先使用 API 返回的 msg
            const errorMsg = error?.response?.data?.msg || 
                           error?.response?.msg || 
                           error?.msg || 
                           error?.message || 
                           '合并失败，请重试';
            showError(errorMsg);
        } finally {
            setIsMerging(false);
        }
    };

    const handleSourceActionBarAction = (action: string) => {
        console.log(`执行源语言操作: ${action}`);
        if (action === 'add-to-voice-library') {
            // 🍎 打开弹窗前，从 localStorage 读取保存的角色列表并渲染（只使用本地列表）
            const finalSeriesId = urlSeriesId ? parseInt(urlSeriesId) : seriesId;
            const finalLangCode = urlLangCode || langCode;
            
            if (finalSeriesId && finalLangCode) {
                const storageKey = `seriesSpeakers_${finalSeriesId}_${finalLangCode}`;
                try {
                    const savedListStr = localStorage.getItem(storageKey);
                    if (savedListStr) {
                        const savedList = JSON.parse(savedListStr);
                        if (Array.isArray(savedList)) {
                            // 🍎 只使用本地角色列表进行渲染
                            setLocalSeriesSpeakers(savedList);
                            console.log('🍎 从本地加载角色列表（打开弹窗）:', savedList);
                        }
                    }
                } catch (error) {
                    console.error('🍎 从本地读取角色列表失败:', error);
                }
            }
            
            setShowAddToVoiceLibraryModal(true);
        } else if (action === 'remove-from-voice-library') {
            // 移出音色库
            handleRemoveFromVoiceLibrary();
        } else if (action === 'add-segment') {
            handleAddSegment();
        } else if (action === 'split-segment') {
            handleSplitSubtitle();
        } else if (action === 'merge-segment') {
            handleMergeSubtitle();
        } else {
            // 执行操作后不自动关闭操作条，让用户可以通过点击外部关闭
            // 操作条会通过点击外部区域关闭
        }
    };

    // 处理调整翻译选项
    const handleAdjustOption = (option: string) => {
        console.log(`选择调整翻译选项: ${option}`);
        
        // 这里可以添加具体的调整翻译逻辑
        switch (option) {
            case 'longer':
                // 立即关闭下拉框
                setShowAdjustDropdown(false);
                handleRetranslate('更加长一些');
                break;
            case 'shorter':
                // 立即关闭下拉框
                setShowAdjustDropdown(false);
                handleRetranslate('更加短一些');
                break;
            case 'custom':
                if (showCustomCard) {
                    // 如果卡片已显示，则隐藏卡片但保持下拉框显示
                    setShowCustomCard(false);
                } else {
                    // 如果卡片未显示，则显示卡片并保持下拉框显示
                    setShowCustomCard(true);
                }
                // 保持下拉框显示，不调用setShowAdjustDropdown(false)
                break;
        }
    };

    // 重新翻译处理函数
    const handleRetranslate = useCallback(async (feedback?: string) => {
        // 如果已经有请求在进行中，直接返回
        if (retranslateRequestRef.current) {
            console.log('已有请求在进行中，跳过重复请求');
            return;
        }
        
        console.log('开始重新翻译，feedback:', feedback);
        setIsRetranslating(true);
        
        // 创建请求Promise并保存到ref
        const requestPromise = (async () => {
            try {
                // 使用targetData的id作为subtitle_id
                const response = await retranslateSubtitleApi(targetData.id, feedback);
                
                console.log('API响应数据:', response);
                console.log('当前targetData:', targetData);
                console.log('当前sourceData:', sourceData);
                
                // 使用 onSubtitleUpdate 回调处理重新翻译结果（避免触发额外的更新接口）
                if (onSubtitleUpdate) {
                    console.log('调用onSubtitleUpdate回调函数，更新重新翻译后的数据');
                    // 标记操作类型为重新翻译
                    (response as any).operationType = 'retranslate';
                    onSubtitleUpdate(response, null);
                } else {
                    console.warn('onSubtitleUpdate回调函数不存在，使用onTargetDataChange');
                    // 如果没有 onSubtitleUpdate，回退到旧方式（但会触发更新接口）
                    if (onTargetDataChange) {
                        const newTargetData = {
                            ...targetData,
                            text: response.post_text,
                            timeRange: `${response.post_start_time} → ${response.post_end_time}`
                        };
                        onTargetDataChange(newTargetData);
                    }
                }
                
                // 更新本地状态（UI显示）
                setTargetText(response.post_text);
                setSourceText(response.origin_text || sourceData.text);
                
                // 清空自定义文本
                setCustomText('');
                
                console.log('重新翻译成功:', response);
            } catch (error) {
                console.error('重新翻译失败:', error);
                // 这里可以添加错误提示
            } finally {
                setIsRetranslating(false);
                retranslateRequestRef.current = null; // 清除请求引用
            }
        })();
        
        retranslateRequestRef.current = requestPromise;
        await requestPromise;
    }, [targetData.id, onTargetDataChange, onSourceDataChange]);

    // 自定义翻译处理函数
    const handleCustomRetranslate = useCallback(() => {
        // 如果自定义文本为空，不触发翻译接口
        if (!customText || !customText.trim()) {
            return;
        }
        // 立即关闭下拉框和卡片
        setShowAdjustDropdown(false);
        setShowCustomCard(false);
        handleRetranslate(customText);
    }, [handleRetranslate, customText]);

    // 留言编辑相关函数
    const handleCommentClick = () => {
        setIsEditingComment(true);
    };

    const handleCommentSave = async () => {
        // 检查留言内容是否有变化
        const trimmedLocalText = (localCommentText || '').trim();
        const trimmedOriginalText = (commentText || '').trim();
        
        if (trimmedLocalText === trimmedOriginalText) {
            // 留言内容没有变化，直接关闭编辑状态，不调用接口
            console.log('留言内容未更改，跳过接口调用');
            setIsEditingComment(false);
            return;
        }
        
        try {
            // 调用新的留言接口
            const response = await addRemarkApi(sourceData.id, localCommentText);
            
            // 检查响应状态
            if (response.code === 0) {
                // 留言成功
                showSuccess('留言成功');
                
                // 关闭编辑状态
                setIsEditingComment(false);
                
                // 同步到本地数据：如果父组件有回调，调用它
                if (onCommentChange) {
                    onCommentChange(sourceData.id, localCommentText);
                }
                
                // 如果有刷新字幕列表的回调，调用它以确保数据同步
                if (onRefreshSubtitles) {
                    try {
                        await onRefreshSubtitles();
                        console.log('✅ 已刷新字幕列表，留言数据已同步');
                    } catch (error) {
                        console.error('❌ 刷新字幕列表失败:', error);
                    }
                }
                
                console.log('✅ 留言成功:', { subtitleId: sourceData.id, remark: localCommentText });
            } else {
                // 留言失败，显示错误信息（使用响应中的msg）
                const errorMsg = response.msg || '留言失败，请重试';
                showError(errorMsg);
                console.error('❌ 留言失败:', { code: response.code, msg: response.msg });
                // 保持编辑状态让用户重试
            }
        } catch (error: any) {
            console.error('❌ 留言接口调用失败:', error);
            // 尝试从错误对象中获取msg
            const errorMsg = error?.response?.msg || error?.response?.data?.msg || error?.msg || error?.message || '留言失败，请重试';
            showError(errorMsg);
            // 保持编辑状态让用户重试
        }
    };

    const handleCommentChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
        setLocalCommentText(e.target.value);
    };

    // 悬停处理
    const handleDialogueHover = (event: React.MouseEvent, isEntering: boolean) => {
        const dialogueContent = event.currentTarget as HTMLElement;
        if (isEntering) {
            dialogueContent.classList.add('hovering');
            // 进入悬停时静默预取历史记录，用于决定是否展示“历史”图标（>=2条才展示）
            void ensureHistoryPrefetched();
            return;
        } else {
            dialogueContent.classList.remove('hovering');
        }
    };


    // 剧集音色库处理
    const handleVoiceLibraryClick = async () => {
        setShowVoiceLibraryModal(true);
        await fetchSeriesSpeakers();
    };

    // 获取剧集说话人数据
    const fetchSeriesSpeakers = async () => {
        try {
            setLoadingSeriesSpeakers(true);
            
            // 如果在更换音色弹窗中，使用新接口 /video/subtitles/query-by-task
            if (showChangeVoiceModal && subtitleData) {
                const taskId = getSubtitleField.taskId(subtitleData);
                if (!taskId) {
                    console.error('缺少taskId参数');
                    setSeriesSpeakers([]);
                    return;
                }

                console.log('🔍 更换音色弹窗：开始获取剧集音色库数据:', { taskId });
                const response = await queryByTaskApi(taskId);
                console.log('📦 接口返回数据:', response);

                // 处理新接口响应格式 { code, data, msg }
                if (response && response.code === 0 && Array.isArray(response.data)) {
                    // 将新接口返回的数据转换为 Speakers.Speaker 格式
                    const speakersData: Speakers.Speaker[] = response.data.map((item: any) => ({
                        id: item.id,
                        series_id: item.seriesId,
                        task_id: taskId,
                        speaker_name: item.speakerName,
                        description: item.description || '',
                        is_series_speaker: item.isSeriesSpeaker || true,
                        subtitles: (item.subtitleRespVOS || []).map((sub: any) => ({
                            id: sub.id,
                            task_id: taskId,
                            origin_text: sub.originText,
                            post_text: '',
                            post_start_time: sub.originStartTime,
                            post_end_time: sub.originEndTime,
                            origin_start_time: sub.originStartTime,
                            origin_end_time: sub.originEndTime,
                            is_episode_voice: false,
                            origin_audio: sub.originAudio,
                            trans_audio: '',
                            subtitle_id: sub.id,
                            score: sub.score,
                            audio_speed: 1.0,
                            volume_gain_db: 1.0
                        }))
                    }));

                    setSeriesSpeakers(speakersData);
                    console.log('✅ 已设置说话人列表，数量:', speakersData.length);
                    
                    // 初始化展开状态，默认全部收起
                    const initialExpandedRoles: { [key: string]: boolean } = {};
                    speakersData.forEach((speaker) => {
                        initialExpandedRoles[speaker.speaker_name] = false;
                    });
                    setExpandedRoles(initialExpandedRoles);
                } else {
                    console.warn('⚠️ 响应格式不符合预期:', response);
                    setSeriesSpeakers([]);
                }
            } else {
                // 非更换音色弹窗，使用原有接口
                if (!seriesId) {
                    console.error('缺少seriesId参数');
                    setSeriesSpeakers([]);
                    return;
                }
                // 后端要求必须带 langCode；缺少则不调用该接口
                if (!langCode) {
                    setSeriesSpeakers([]);
                    return;
                }

                console.log('🔍 开始获取剧集说话人数据:', { seriesId, langCode });
                const response = await getSeriesSpeakersApi(seriesId, langCode);
                console.log('📦 接口返回数据:', response);

                // 处理API响应数据
                let speakersData = null;

                // 情况1: 标准格式 {success: true, data: [...]}
                if (response && typeof response === 'object' && 'success' in response && response.success && response.data && Array.isArray(response.data)) {
                    speakersData = response.data;
                    console.log('✅ 使用标准格式，说话人数量:', speakersData.length);
                }
                // 情况2: 直接返回数组格式 [...]
                else if (Array.isArray(response)) {
                    speakersData = response;
                    console.log('✅ 使用数组格式，说话人数量:', speakersData.length);
                } else {
                    console.warn('⚠️ 响应格式不符合预期:', response);
                }

                if (speakersData && Array.isArray(speakersData)) {
                    setSeriesSpeakers(speakersData);
                    console.log('✅ 已设置说话人列表，数量:', speakersData.length);
                    // 初始化展开状态，默认全部收起
                    const initialExpandedRoles: { [key: string]: boolean } = {};
                    speakersData.forEach((speaker) => {
                        initialExpandedRoles[speaker.speaker_name] = false;
                    });
                    setExpandedRoles(initialExpandedRoles);
                } else {
                    console.warn('⚠️ speakersData 为空或不是数组，设置为空数组');
                    setSeriesSpeakers([]);
                }
            }

        } catch (error) {
            console.error('❌ 获取剧集说话人数据失败:', error);
            setSeriesSpeakers([]);
        } finally {
            setLoadingSeriesSpeakers(false);
        }
    };

    const handleVoiceLibraryCancel = () => {
        setShowVoiceLibraryModal(false);
        setSelectedVoiceClip(null); // 清除选中状态
    };

    const handleVoiceLibraryConfirm = () => {
        console.log('确认使用选择的音色');
        setShowVoiceLibraryModal(false);
        // 这里可以添加使用音色的具体逻辑
    };

    // 切换角色展开状态
    const toggleRoleExpansion = (roleName: string) => {
        setExpandedRoles(prev => ({
            ...prev,
            [roleName]: !prev[roleName]
        }));
    };

    // 播放音色片段
    const handlePlayVoiceClip = (clipText: string, audioUrl?: string, subtitleId?: number) => {
        if (!audioUrl) {
            return;
        }

        // 停止当前播放的音频
        if (audioRef.current) {
            audioRef.current.pause();
            audioRef.current = null;
        }

        // 如果点击的是同一个音频，则停止播放
        const currentClipId = `${subtitleId || clipText}`;
        if (playingVoiceClipId === currentClipId) {
            setPlayingVoiceClipId(null);
            return;
        }

        try {
            // 构建完整的音频URL（使用非缓存路径，因为无法确定是否是 trans_audio）
            const fullAudioUrl = processVideoUrl(audioUrl);

            const audio = new Audio(fullAudioUrl);
            audioRef.current = audio;
            setPlayingVoiceClipId(currentClipId);

            // 播放音频
            audio.play().catch(error => {
                console.error('播放音频失败:', error);
                setPlayingVoiceClipId(null);
                audioRef.current = null;
            });

            // 音频播放结束时的处理
            audio.addEventListener('ended', () => {
                setPlayingVoiceClipId(null);
                audioRef.current = null;
            });

            // 音频播放错误时的处理
            audio.addEventListener('error', () => {
                setPlayingVoiceClipId(null);
                audioRef.current = null;
            });

        } catch (error) {
            console.error('播放音频失败:', error);
            setPlayingVoiceClipId(null);
        }
    };

    // 使用音色片段（仅选中，不调用接口）
    const handleUseVoiceClip = (subtitle: Speakers.Subtitle) => {
        // 只设置选中状态，不调用接口
        // 设置剧集音色库选中的片段
        setSelectedVoiceClip(subtitle);
        
        // 清除说话人其他片段的选中状态（取消勾选）
        setSelectedSegment(null);
        
        // 将选中的片段转换为GetSubtitleResponse格式，用于在更换音色弹窗顶部显示
        if (subtitleData) {
            const displaySubtitleData = {
                ...subtitleData,
                origin_text: subtitle.origin_text,
                origin_start_time: subtitle.origin_start_time,
                origin_end_time: subtitle.origin_end_time,
                origin_audio: subtitle.origin_audio,
                voice_info: {
                    origin_text: subtitle.origin_text,
                    origin_start_time: subtitle.origin_start_time,
                    origin_end_time: subtitle.origin_end_time,
                    origin_audio: subtitle.origin_audio,
                    score: subtitle.score || 0
                }
            } as unknown as Subtitles.GetSubtitleResponse;
            // 更新subtitleData以在弹窗顶部显示选中的片段
            setSubtitleData(displaySubtitleData);
        }
        
        console.log('🎤 选中剧集音色库台词:', {
            origin_text: subtitle.origin_text,
            subtitle_id: subtitle.subtitle_id,
            time_range: `${subtitle.origin_start_time} → ${subtitle.origin_end_time}`,
            origin_audio: subtitle.origin_audio,
            score: subtitle.score
        });

        // 关闭剧集音色库弹窗
        setShowVoiceLibraryModal(false);
    };

    // 添加至剧集音色库处理
    const handleAddToVoiceLibraryCancel = () => {
        setShowAddToVoiceLibraryModal(false);
        setSelectedRoleForVoiceLibrary('');
    };

    // 获取字幕数据（每次点击更换音色时重新获取最新数据）
    const handleGetSubtitleData = async () => {
        try {
            setLoadingSubtitle(true);
            setLoadingSpeakerSubtitles(true);
            setSelectedSegment(null); // 重置选中的片段
            setSelectedVoiceClip(null); // 重置剧集音色库的选中状态

            // 使用sourceData.subtitle_id（来自/video/subtitles/list接口）
            // 每次打开弹窗时都重新获取最新数据，确保显示的是最新的音色
            if (!sourceData.subtitle_id) {
                console.error('❌ 缺少subtitle_id，无法获取字幕数据。sourceData:', sourceData);
                showError('缺少字幕ID，请刷新页面重试');
                setLoadingSubtitle(false);
                setLoadingSpeakerSubtitles(false);
                return;
            }
            
            const subtitleId = sourceData.subtitle_id;
            console.log('🎤 获取字幕数据，使用台词的subtitle_id:', subtitleId, 'sourceData:', { id: sourceData.id, subtitle_id: sourceData.subtitle_id, '说明': '每次打开弹窗都重新获取最新数据' });

            // 每次点击更换音色时，重新获取最新的字幕数据
            const subtitleResponse = await getSubtitleByIdApi(subtitleId);
            console.log('🎵 获取到的字幕数据:', {
                subtitleId: subtitleId,
                response_id: subtitleResponse.id,
                response_subtitle_id: subtitleResponse.subtitle_id,
                voice_info: subtitleResponse.voice_info,
                origin_audio: subtitleResponse.origin_audio
            });
            setSubtitleData(subtitleResponse);

            // 使用新接口获取换音色选项列表（已包含说话人信息）
            // 参数使用选择台词的id值（sourceData.id），而不是subtitle_id
            const selectedSubtitleId = sourceData.id;
            console.log('🎤 获取换音色选项列表，使用选择台词的id:', selectedSubtitleId);
            const changeToneOptionsResponse = await getChangeToneOptionsApi(selectedSubtitleId);

            // 将新接口返回的数据转换为 GetSubtitleResponse 格式（保持兼容性）
            const taskId = getSubtitleField.taskId(subtitleResponse);
            const speaker = getSubtitleField.speaker(subtitleResponse) as Subtitles.Speaker;
            const convertedSubtitles: Subtitles.GetSubtitleResponse[] = changeToneOptionsResponse.data.map(option => ({
                task_id: taskId || 0,
                origin_text: option.originText,
                post_text: '', // 新接口没有此字段
                post_start_time: '', // 新接口没有此字段
                post_end_time: '', // 新接口没有此字段
                origin_start_time: option.originStartTime,
                origin_end_time: option.originEndTime,
                // origin_post_time: 0, // 新接口没有此字段，GetSubtitleResponse 类型中也没有此字段
                is_episode_voice: getSubtitleField.isEpisodeVoice(subtitleResponse) ?? false,
                origin_audio: option.originAudio,
                trans_audio: getSubtitleField.transAudio(subtitleResponse) || '',
                subtitle_id: option.id,
                score: option.score,
                audio_speed: getSubtitleField.audioSpeed(subtitleResponse) ?? 1.0,
                volume_gain_db: getSubtitleField.volumeGainDb(subtitleResponse) ?? 1.0,
                id: option.id,
                speakers: {
                    id: speaker?.id || 0,
                    series_id: speaker?.series_id || 0,
                    task_id: taskId || 0,
                    speaker_name: option.speakerName || speaker?.speaker_name || '',
                    description: option.description || speaker?.description || '',
                    is_series_speaker: option.isSeriesSpeaker !== undefined ? option.isSeriesSpeaker : (speaker?.is_series_speaker ?? false)
                },
                voice_info: {
                    origin_text: option.originText,
                    origin_start_time: option.originStartTime,
                    origin_end_time: option.originEndTime,
                    origin_audio: option.originAudio,
                    score: option.score
                },
                words: [] // 新接口没有此字段
            }));

            // 按score从高到低排序
            const sortedSubtitles = convertedSubtitles.sort((a, b) => b.score - a.score);
            setSpeakerSubtitles(sortedSubtitles);

        } catch (error) {
            console.error('获取字幕数据失败:', error);
        } finally {
            setLoadingSubtitle(false);
            setLoadingSpeakerSubtitles(false);
        }
    };

    // 语音条相关事件处理函数
    const handlePlayTargetAudio = async () => {
        // 如果在更换音色弹窗中，优先播放原音频（因为弹窗显示的是原文本）
        // 否则优先使用 trans_audio（目标台词音频），其次是 voice_info.origin_audio，最后是 origin_audio
        // 注意：重新配音后的音频在 trans_audio 字段中
        let audioPath: string | undefined;
        const voiceInfo = getSubtitleField.voiceInfo(subtitleData);
        const originAudio = getSubtitleField.originAudio(subtitleData);
        const transAudio = getSubtitleField.transAudio(subtitleData);
        if (showChangeVoiceModal) {
            // 更换音色弹窗中，优先播放原音频
            audioPath = voiceInfo?.origin_audio || originAudio || transAudio;
        } else {
            // 正常播放，优先使用翻译后的音频
            audioPath = transAudio || voiceInfo?.origin_audio || originAudio;
        }
        
        if (!audioPath) {
            console.log('没有音频数据可播放');
            return;
        }

        try {
            const audioId = `target_audio_${targetData.id}`;
            
            // 如果正在播放，则停止
            if (isPlayingAudio && audioManager.isPlaying(audioId)) {
                audioManager.stopSound(audioId);
                setIsPlayingAudio(false);
                setPlayingSegmentId(null);
                return;
            }

            // 停止并销毁旧实例，防止缓存旧源
            audioManager.stopSound(audioId);
            audioManager.destroySound(audioId);

            // 构建完整的音频URL
            // 判断是否是 trans_audio（内容哈希命名，可使用强缓存）
            const isTransAudio = audioPath === transAudio;
            let baseAudioUrl: string;
            if (audioPath.startsWith('http://') || audioPath.startsWith('https://')) {
                baseAudioUrl = audioPath;
            } else if (isTransAudio) {
                // trans_audio 使用内容哈希命名，可以使用强缓存（不需要时间戳）
                baseAudioUrl = processTransAudioUrl(audioPath);
            } else {
                // 其他音频（origin_audio 等）不使用强缓存，添加时间戳避免缓存
                baseAudioUrl = processVideoUrl(audioPath);
                const ts = Date.now();
                baseAudioUrl = `${baseAudioUrl}${baseAudioUrl.includes('?') ? `&t=${ts}` : `?t=${ts}`}`;
            }
            const audioUrl = baseAudioUrl;

            // 获取当前语速和音量设置
            // 如果是原始音频origin_audio，不应用倍速
            const voiceInfo = getSubtitleField.voiceInfo(subtitleData);
            const originAudio = getSubtitleField.originAudio(subtitleData);
            const isOriginAudio = audioPath === originAudio || audioPath === voiceInfo?.origin_audio;
            const audioSpeed = isOriginAudio ? 1.0 : (onGetCurrentSpeed ? onGetCurrentSpeed(targetData.id) : (getSubtitleField.audioSpeed(subtitleData) ?? 1.0));
            const volumeGain = onGetCurrentVolume ? onGetCurrentVolume(targetData.id) : (getSubtitleField.volumeGainDb(subtitleData) ?? 1.0);

            // 使用 AudioManager 创建音频实例
            // 如果是原始音频，不应用倍速；否则使用台词数据中的audio_speed值
            const sound = audioManager.createSound(audioId, audioUrl, {
                volume: 1.0,
                rate: audioSpeed, // 原始音频不应用倍速，其他音频使用台词数据中的audio_speed值
                preload: true,
                html5: true
            });

            // 监听播放结束事件，重置播放状态
            sound.once('end', () => {
                setIsPlayingAudio(false);
                setPlayingSegmentId(null);
            });

            await audioManager.playSound(audioId, volumeGain, 0);
            
            setIsPlayingAudio(true);
            setPlayingSegmentId(null);

            console.log('目标台词音频播放成功（Howler.js）', {
                audioId,
                audioSpeed,
                volumeGain,
                audioUrl
            });

        } catch (error) {
            setIsPlayingAudio(false);
            setPlayingSegmentId(null);
            console.error('播放音频失败:', error);
        }
    };

    const handleToggleSegmentList = () => {
        setShowSegmentList(!showSegmentList);
    };

    const handlePlaySegmentAudio = async (segmentData?: Subtitles.GetSubtitleResponse) => {
        if (!segmentData?.origin_audio) {
            console.log('没有音频数据可播放');
            return;
        }

        try {
            const audioId = `segment_audio_${segmentData.id}`;
            
            // 如果正在播放这个片段，则停止
            if (playingSegmentId === segmentData.id && audioManager.isPlaying(audioId)) {
                audioManager.stopSound(audioId);
                setPlayingSegmentId(null);
                setIsPlayingAudio(false);
                return;
            }

            // 停止其他可能正在播放的音频
            audioManager.stopSound(audioId);
            audioManager.destroySound(audioId);

            // 构建完整的音频URL（origin_audio 不使用强缓存，添加时间戳避免缓存）
            const originAudio = getSubtitleField.originAudio(segmentData) || '';
            const ts = Date.now();
            let baseAudioUrl: string;
            if (originAudio.startsWith('http://') || originAudio.startsWith('https://')) {
                baseAudioUrl = originAudio;
            } else {
                baseAudioUrl = processVideoUrl(originAudio);
            }
            baseAudioUrl = `${baseAudioUrl}${baseAudioUrl.includes('?') ? `&t=${ts}` : `?t=${ts}`}`;
            const audioUrl = `${baseAudioUrl}${baseAudioUrl.includes('?') ? `&t=${ts}` : `?t=${ts}`}`;

            // 获取当前语速和音量设置
            // 如果是原始音频origin_audio，不应用倍速
            const audioSpeed = 1.0; // 原始音频不应用倍速
            const volumeGain = onGetCurrentVolume ? onGetCurrentVolume(segmentData.id) : (getSubtitleField.volumeGainDb(segmentData) ?? 1.0);

            // 使用 AudioManager 创建音频实例（参考测试案例）
            const sound = audioManager.createSound(audioId, audioUrl, {
                volume: 1.0, // 基础音量，实际音量通过 gain 控制
                rate: audioSpeed, // 原始音频不应用倍速
                preload: true,
                html5: true // 强制使用 HTML5 模式
            });

            // 监听播放结束事件，重置播放状态
            sound.once('end', () => {
                setPlayingSegmentId(null);
                setIsPlayingAudio(false);
            });

            // 播放音频并设置音量增益
            await audioManager.playSound(audioId, volumeGain, 0);
            
            setPlayingSegmentId(segmentData.id);
            setIsPlayingAudio(false); // 清除主语音条播放状态

            console.log('片段音频播放成功（Howler.js）', {
                audioId,
                audioSpeed,
                volumeGain,
                audioUrl
            });

        } catch (error) {
            setPlayingSegmentId(null);
            setIsPlayingAudio(false);
            console.error('播放音频失败:', error);
        }
    };

    // 更换音色处理函数
    const handleChangeVoice = (selectedSubtitle: Subtitles.GetSubtitleResponse) => {
        // 只设置选中的片段，不调用API
        setSelectedSegment(selectedSubtitle);
        
        // 清除剧集音色库的选中状态（取消勾选），确保两个选择互斥
        setSelectedVoiceClip(null);
    };

    // 确认更换音色
    const handleConfirmChangeVoice = async () => {
        if (!subtitleData || changingVoice) {
            return;
        }

        // 检查是否有选中的片段（可能是更换音色弹窗中的片段，也可能是剧集音色库弹窗中的片段）
        const selectedSubtitle = selectedSegment || selectedVoiceClip;
        
        // 如果没有选中片段，只是关闭弹窗
        if (!selectedSubtitle) {
            setShowChangeVoiceModal(false);
            setShowVoiceLibraryModal(false);
            setSelectedSegment(null);
            setSelectedVoiceClip(null);
            return;
        }

        try {
            setChangingVoice(true);

            // 清理音频播放状态，防止状态不一致
            const audioId = `target_audio_${targetData.id}`;
            if (audioManager.isPlaying(audioId)) {
                audioManager.stopSound(audioId);
                audioManager.destroySound(audioId);
            }
            setIsPlayingAudio(false);
            setPlayingSegmentId(null);

            // 构建更换音色参数
            // subtitleId: 原台词id值（点击的台词的id）
            // selectedSubtitleId: 选中的id值（选中的音色片段的id）
            // 无论是从剧集音色库选中的片段，还是从更换音色弹窗选中的片段，都使用id
            const selectedSubtitleId = selectedSubtitle.id;
            
            if (!sourceData.id || !selectedSubtitleId) {
                showError('缺少字幕ID');
                return;
            }

            // 参数说明：
            // subtitleId: 原台词id值（点击的台词的id，使用sourceData.id）
            // selectedSubtitleId: 选中的id值（选中的音色片段的id，无论是剧集音色库还是更换音色弹窗都使用id）
            // applyToAll: 是否应用到所有片段
            const changeVoiceParams = {
                subtitleId: sourceData.id,                // 原台词id值（点击的台词的id）
                selectedSubtitleId: selectedSubtitleId,   // 选中的id值（选中的音色片段的id）
                applyToAll: applyToAllSegments           // 是否应用到所有片段
            };

            // 输出上传参数到控制台
            console.log('🔄 更换音色参数:', {
                subtitleId: changeVoiceParams.subtitleId,        // 原台词id值（点击的台词的id）
                selectedSubtitleId: changeVoiceParams.selectedSubtitleId,  // 选中的id值（选中的音色片段的id）
                applyToAll: changeVoiceParams.applyToAll,        // 是否应用到所有片段
                说明: '将原台词（subtitleId）的音色更换为选中片段（selectedSubtitleId）的音色'
            });

            // 调用更换音色API（新接口会自动处理 applyToAll 逻辑）
            const response = await changeVoiceApi(changeVoiceParams);

            console.log('✅ 更换音色API响应:', {
                code: response.code,
                data: response.data,
                msg: response.msg
            });

            // 如果成功，按顺序执行：刷新字幕列表 → 更新本地数据 → 关闭弹窗
            if (response.code === 0) {
                // 显示成功提示
                showSuccess('更换音色成功');
                
                // 步骤1: 立即重新调用/video/subtitles/list接口，更新本地数据
                if (onRefreshSubtitles) {
                    try {
                        console.log('🔄 开始重新获取字幕列表，更新本地数据...');
                        // 等待刷新完成，确保数据已更新
                        await onRefreshSubtitles();
                        console.log('✅ 已重新获取字幕列表，本地数据已更新');
                        
                        // 步骤2: 数据更新后，重新获取最新的字幕详情（使用更新后的sourceData.subtitle_id）
                        // 注意：此时sourceData可能还未更新，需要等待父组件更新后再调用
                        // 但由于sourceData是props，会在父组件更新后自动更新
                        // 所以下次打开弹窗时，handleGetSubtitleData会使用最新的sourceData.subtitle_id调用getSubtitleByIdApi
                        
                    } catch (error) {
                        console.error('❌ 重新获取字幕列表失败:', error);
                        showError('刷新字幕列表失败，请手动刷新页面');
                    }
                }
                
                // 步骤3: 数据更新完成后，关闭弹窗并清除选中状态
                setShowChangeVoiceModal(false);
                setShowVoiceLibraryModal(false);
                setSelectedSegment(null);
                setSelectedVoiceClip(null);
                setApplyToAllSegments(false); // 重置复选框状态
                setSubtitleData(null); // 清空字幕数据，确保下次打开弹窗时重新获取最新数据
                
            } else {
                // 请求失败，显示错误信息
                showError(response.msg || '更换音色失败');
            }

        } catch (error: any) {
            console.error('更换音色失败:', error);
            showError(error?.message || '更换音色失败，请重试');
        } finally {
            setChangingVoice(false);
        }
    };

    // 取消更换音色
    const handleCancelChangeVoice = () => {
        setShowChangeVoiceModal(false);
        setSelectedSegment(null);
        setSelectedVoiceClip(null); // 清除剧集音色库的选中状态
        setApplyToAllSegments(false); // 重置复选框状态
    };


    const handleAddToVoiceLibraryConfirm = async () => {
        if (!selectedRoleForVoiceLibrary || !onPromoteToSeriesSpeaker) {
            return;
        }

        try {
            setIsAddingToVoiceLibrary(true);
            // 使用本地角色列表（localSeriesSpeakers）来查找匹配的角色，因为可能包含新创建的角色
            const matchedSpeaker = localSeriesSpeakers.find(
                speaker => speaker.label === selectedRoleForVoiceLibrary
            );

            if (matchedSpeaker && matchedSpeaker.id) {
                await onPromoteToSeriesSpeaker(matchedSpeaker.id, sourceData.id);

                // 添加成功后，只更新本地状态，不更新videoData
                console.log('添加至音色库成功，更新角色名:', selectedRoleForVoiceLibrary);

                // 更新剧集台词标记
                setIsSeriesSpeaker(true);

                // 重新获取字幕数据以同步状态，确保subtitleData.speakers被正确更新
                try {
                    const subtitleId = sourceData.id;
                    const updatedSubtitleData = await getSubtitleByIdApi(subtitleId);
                    setSubtitleData(updatedSubtitleData);
                    console.log('添加至音色库成功，已同步字幕数据');
                } catch (error) {
                    console.error('同步字幕数据失败:', error);
                }

                // 通知父组件更新角色选择状态，但不更新videoData
                if (onRoleSelect) {
                    // 调用onRoleSelect并传递isSeriesVoiceLibrary=true，这样父组件就不会更新videoData
                    onRoleSelect(selectedRoleForVoiceLibrary, sourceData.id, undefined, true);
                }

                console.log('✅ 已更新本地角色选择状态，避免videoData更新导致视频重新加载');
            } else {
                console.error('未找到匹配的角色或角色ID不存在:', selectedRoleForVoiceLibrary);
                showError('未找到匹配的角色，请重新选择');
            }
        } catch (error: any) {
            console.error('添加至剧集音色库失败:', error);
            showError(error?.message || '添加至剧集音色库失败，请重试');
        } finally {
            setIsAddingToVoiceLibrary(false);
            setShowAddToVoiceLibraryModal(false);
            setSelectedRoleForVoiceLibrary('');
        }
    };

    const handleRoleSelectForVoiceLibrary = (roleName: string) => {
        setSelectedRoleForVoiceLibrary(roleName);
    };

    const handleRemoveSeriesSpeakerRole = async (speakerId: number) => {
        if (!speakerId || deletingRoleId === speakerId) {
            return;
        }
        try {
            setDeletingRoleId(speakerId);
            await deleteSpeakerApi(speakerId);

            // 本地同步：从弹窗角色列表移除
            setLocalSeriesSpeakers(prev => {
                const updatedList = prev.filter(r => r.id !== speakerId);
                
                // 🍎 更新本地备份的角色列表
                const finalSeriesId = urlSeriesId ? parseInt(urlSeriesId) : seriesId;
                const finalLangCode = urlLangCode || langCode;
                if (finalSeriesId && finalLangCode) {
                    const storageKey = `seriesSpeakers_${finalSeriesId}_${finalLangCode}`;
                    try {
                        localStorage.setItem(storageKey, JSON.stringify(updatedList));
                        console.log('🍎 删除角色后更新本地备份:', updatedList);
                    } catch (error) {
                        console.error('🍎 更新本地角色列表失败:', error);
                    }
                }
                
                return updatedList;
            });

            // 如果当前选中的就是被删的角色，清空选中
            setSelectedRoleForVoiceLibrary(prev => {
                const currentSelected = prev;
                const removed = localSeriesSpeakers.find(r => r.id === speakerId);
                if (removed && removed.label === currentSelected) {
                    return '';
                }
                return currentSelected;
            });

            // 通知父组件同步删除（让其他 RowItem / 下次打开弹窗也一致）
            if (onDeleteSeriesSpeaker) {
                onDeleteSeriesSpeaker(speakerId);
            }

            showSuccess('已移出该角色');
        } catch (e: any) {
            console.error('移出剧集说话人角色失败:', e);
            showError(e?.message || '移出失败，请重试');
        } finally {
            setDeletingRoleId(null);
        }
    };

    // 开始创建角色
    const handleStartCreateRole = () => {
        setIsCreatingRole(true);
        setNewRoleName('');
    };

    // 确认创建角色
    const handleConfirmCreateRole = async () => {
        if (!newRoleName.trim()) {
            // 如果输入为空，直接恢复到原始状态
            setIsCreatingRole(false);
            setNewRoleName('');
            return;
        }
        try {
            // 优先使用 URL 参数，其次使用 props 传入的值
            const finalSeriesId = urlSeriesId ? parseInt(urlSeriesId) : seriesId;
            const finalLangCode = urlLangCode || langCode;
            
            if (!finalSeriesId || !finalLangCode) {
                showError('缺少剧集ID或语言代码');
                return;
            }
            
            const params: Speakers.CreateSpeakerParams = {
                seriesId: finalSeriesId,
                speakerName: newRoleName.trim(),
                langCode: finalLangCode
            };
            const res = await createSpeakerApi(params);
            const created = (res as any)?.data as Speakers.Speaker | undefined;
            if (created && created.id) {
                // 创建成功后，同步更新本地角色列表
                const newRole = {
                    key: `series_speaker_${created.id}`,
                    label: created.speaker_name,
                    id: created.id,
                    description: created.description || ''
                };
                
                // 更新本地状态并保存到 localStorage
                setLocalSeriesSpeakers(prev => {
                    const updatedList = [...prev, newRole];
                    
                    // 🍎 将最新的角色列表存到本地
                    const storageKey = `seriesSpeakers_${finalSeriesId}_${finalLangCode}`;
                    try {
                        localStorage.setItem(storageKey, JSON.stringify(updatedList));
                        // 🍎 在控制台输出最新角色列表
                        console.log('🍎 最新角色列表已保存到本地:', updatedList);
                    } catch (error) {
                        console.error('🍎 保存角色列表到本地失败:', error);
                    }
                    
                    return updatedList;
                });
                
                showSuccess((res as any)?.message || `新建说话人成功：${created.speaker_name}`);
                // 创建成功后自动选中新创建的角色
                setSelectedRoleForVoiceLibrary(created.speaker_name);
            } else {
                showError('创建角色失败，请重试');
            }
        } catch (e: any) {
            showError(e?.message || '创建角色失败');
        } finally {
            setIsCreatingRole(false);
            setNewRoleName('');
        }
    };

    // 移出音色库处理函数
    const handleRemoveFromVoiceLibrary = async () => {
        const speaker = getSubtitleField.speaker(subtitleData);
        if (!subtitleData || !speaker) {
            showError('无法获取说话人信息');
            return;
        }

        const subtitleId = sourceData.id;

        try {
            console.log('开始移出音色库:', { subtitleId });
            showAlert('info', '正在移出音色库...');

            // 调用移出音色库接口（仅按台词ID移除）
            const res = await demoteSpeakerSubtitleApi(subtitleId);
            if (!res || res.code !== 0) {
                throw new Error(res?.msg || '移出音色库失败');
            }

            // 更新本地状态
            setIsSeriesSpeaker(false);
            
            // 重新获取字幕数据以同步状态
            try {
                const updatedSubtitleData = await getSubtitleByIdApi(subtitleId);
                setSubtitleData(updatedSubtitleData);
                console.log('移出音色库成功，已同步数据');
            } catch (error) {
                console.error('同步数据失败:', error);
            }

            // 通知父组件更新状态（如果需要）
            if (onRoleSelect) {
                // 可以传递空值或特殊标记表示已移出
                onRoleSelect('', sourceData.id, undefined, false);
            }

            showAlert('success', '已移出音色库');
        } catch (error) {
            console.error('移出音色库失败:', error);
            showError('移出音色库失败，请重试');
        }
    };

    // 删除字幕处理函数
    const handleDeleteSubtitle = async () => {
        console.log('🔥 handleDeleteSubtitle 被调用', { 
            isDeleting, 
            targetDataId: targetData.id,
            targetData: targetData,
            isTemporarySubtitle: isTemporarySubtitle
        });
        
        if (isDeleting) {
            console.log('⚠️ 正在删除中，跳过重复请求');
            return;
        }

        try {
            console.log('🚀 开始执行删除操作');
            setIsDeleting(true);
            
            if (isTemporarySubtitle) {
                // 临时字幕：直接从本地数据中删除，不调用API
                console.log('🗑️ 删除临时字幕，直接从本地数据中移除');
                if (onDeleteSubtitle) {
                    console.log('📢 通知父组件删除临时字幕');
                    onDeleteSubtitle(targetData.id);
                }
            } else {
                // 真实字幕：调用删除API
                console.log('📡 调用删除API，subtitle_id:', targetData.id);
                const response = await deleteSubtitleApi(targetData.id);
                
                console.log('✅ 删除字幕成功:', response);
                
                // 通知父组件删除成功，让父组件处理页面同步显示
                if (onDeleteSubtitle) {
                    console.log('📢 通知父组件删除成功');
                    onDeleteSubtitle(targetData.id);
                } else {
                    console.log('❌ 没有onDeleteSubtitle回调函数');
                }
            }
            
            // 关闭操作条
            setShowActionBar(false);
            
        } catch (error) {
            console.error('❌ 删除字幕失败:', error);
            // 这里可以添加错误提示
        } finally {
            setIsDeleting(false);
        }
    };

    // 初始化输入框高度 - 在组件挂载时设置初始高度
    useEffect(() => {
        const initializeHeight = () => {
            if (sourceDialogueTextRef.current && targetDialogueTextRef.current) {
                // 临时设置高度为auto以获取真实高度
                const originalSourceHeight = sourceDialogueTextRef.current.style.height;
                const originalTargetHeight = targetDialogueTextRef.current.style.height;

                sourceDialogueTextRef.current.style.height = 'auto';
                targetDialogueTextRef.current.style.height = 'auto';

                // 计算源语言文本高度
                const sourceHeight = Math.max(sourceDialogueTextRef.current.scrollHeight, 20);

                // 计算目标语言文本高度
                const targetHeight = Math.max(targetDialogueTextRef.current.scrollHeight, 20);

                // 恢复原始高度
                sourceDialogueTextRef.current.style.height = originalSourceHeight;
                targetDialogueTextRef.current.style.height = originalTargetHeight;

                // 取两者中的较大值，但不超过最大高度
                const maxHeight = Math.max(sourceHeight, targetHeight);
                const finalHeight = Math.min(maxHeight, MAX_EDIT_HEIGHT_PX);

                setInputHeight(finalHeight);
            } else {
                // 如果找不到元素，使用默认高度
                setInputHeight(20);
            }
        };

        // 延迟执行以确保DOM已渲染
        const timer = setTimeout(initializeHeight, 100);
        return () => clearTimeout(timer);
    }, []); // 只在组件挂载时执行一次

    // 初始化剧集台词标记状态
    useEffect(() => {
        // 使用传入的propIsSeriesSpeaker值来初始化状态
        setIsSeriesSpeaker(propIsSeriesSpeaker);
    }, [propIsSeriesSpeaker, sourceData.id, selectedRole]);

    // 同步targetData.timeRange变化到本地时间状态
    useEffect(() => {
        const [newStartTime, newEndTime] = targetData.timeRange.split(' → ');
        setStartTime(newStartTime);
        setEndTime(newEndTime);
    }, [targetData.timeRange]);

    // 同步commentText变化到本地状态
    useEffect(() => {
        setLocalCommentText(commentText);
    }, [commentText]);

    // 当inputHeight变化时，同步更新两个输入框的高度
    useEffect(() => {
        if (inputHeight !== null) {
            // 同步源语言输入框高度
            if (sourceInputRef.current) {
                sourceInputRef.current.style.height = `${inputHeight}px`;
                sourceInputRef.current.style.overflowY = inputHeight >= MAX_EDIT_HEIGHT_PX ? 'auto' : 'hidden';
            }

            // 同步目标语言输入框高度
            if (targetInputRef.current) {
                targetInputRef.current.style.height = `${inputHeight}px`;
                targetInputRef.current.style.overflowY = inputHeight >= MAX_EDIT_HEIGHT_PX ? 'auto' : 'hidden';
            }
        }
    }, [inputHeight]);

    // 点击外部关闭编辑状态和操作条
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (editingField &&
                !sourceInputRef.current?.contains(event.target as Node) &&
                !targetInputRef.current?.contains(event.target as Node)) {
                if (editingField === 'source') {
                    saveEdit('source', sourceText);
                } else if (editingField === 'target') {
                    saveEdit('target', targetText);
                }
            }

            // 恢复点击外部关闭操作条的逻辑
            const actionBar = document.querySelector('.floating-action-bar');
            const sourceActionBar = document.querySelector('.source-action-bar');
            const adjustDropdown = document.querySelector('.adjust-translation-dropdown');

            // 检查是否点击了调整翻译下拉菜单
            const clickedAdjustDropdown = adjustDropdown && adjustDropdown.contains(event.target as Node);

            // 检查是否点击了自定义卡片
            const customCard = document.querySelector('.custom-translation-card');
            const clickedCustomCard = customCard && customCard.contains(event.target as Node);

            // 检查是否点击了Popconfirm
            const popconfirm = document.querySelector('.ant-popconfirm');
            const clickedPopconfirm = popconfirm && popconfirm.contains(event.target as Node);

            // 检查是否点击了操作条本身
            const clickedActionBar = actionBar && actionBar.contains(event.target as Node);
            const clickedSourceActionBar = sourceActionBar && sourceActionBar.contains(event.target as Node);

            // 如果点击了外部区域（不是操作条、下拉菜单、自定义卡片、Popconfirm或输入框），则关闭操作条和下拉框
            if (showActionBar && !clickedActionBar && !clickedAdjustDropdown && !clickedCustomCard && !clickedPopconfirm && !targetInputRef.current?.contains(event.target as Node)) {
                setShowActionBar(false);
                setShowAdjustDropdown(false);
            }
            if (showSourceActionBar && !clickedSourceActionBar && !sourceInputRef.current?.contains(event.target as Node)) {
                setShowSourceActionBar(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [editingField, sourceText, targetText, showActionBar, showSourceActionBar]);

    // 点击外部关闭历史记录面板
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (!isHistoryPanelOpen || !onCloseHistoryPanel) {
                return;
            }

            const target = event.target as HTMLElement;
            
            // 检查是否点击了历史面板内部
            const clickedHistoryPanel = historyPanelRef.current && historyPanelRef.current.contains(target);
            
            // 检查是否点击了历史按钮（需要检查所有历史按钮，因为可能有多个RowItem）
            // 使用 closest 来检查是否点击了历史按钮或其父元素
            const historyIcon = target.closest('.anticon.anticon-history');
            const clickedHistoryButton = !!historyIcon;
            
            // 检查是否点击了 action-buttons 容器（历史按钮的父容器）
            const actionButtons = target.closest('.action-buttons');
            const clickedActionButtons = !!actionButtons;

            // 如果点击了外部区域（不是历史面板、历史按钮或 action-buttons 容器），则关闭面板
            if (!clickedHistoryPanel && !clickedHistoryButton && !clickedActionButtons) {
                // 关闭历史音频播放
                if (historyAudioRef.current) {
                    historyAudioRef.current.pause();
                    historyAudioRef.current.currentTime = 0;
                }
                setActiveHistoryId(null);
                onCloseHistoryPanel();
            }
        };

        if (isHistoryPanelOpen) {
            // 使用 setTimeout 确保在按钮的 onClick 处理之后执行
            const timeoutId = setTimeout(() => {
                document.addEventListener('mousedown', handleClickOutside);
            }, 0);

            return () => {
                clearTimeout(timeoutId);
                document.removeEventListener('mousedown', handleClickOutside);
            };
        }
    }, [isHistoryPanelOpen, onCloseHistoryPanel]);

    // 判断当前台词是否应该高亮
    // 注意：这里需要根据实际的数据结构来判断
    // 如果sourceData.id对应的是subtitle_id，则直接比较
    // 如果sourceData.id对应的是其他ID，则需要从props中获取subtitle_id
    const isHighlighted = highlightedSubtitleId === sourceData.id;

    // 组件卸载时清理音频和请求
    useEffect(() => {
        return () => {
            if (audioRef.current) {
                audioRef.current.pause();
                audioRef.current = null;
            }
            // 清理请求引用
            retranslateRequestRef.current = null;
        };
    }, []);

    return (
        <div
            className={`text-list-item ${isHighlighted ? 'highlighted' : ''} ${isUpdating ? 'updating' : ''}`}
            data-row-id={sourceData.id}
            data-subtitle-id={sourceData.id}
            onClick={(e) => {
                // 点击台词时输出台词的id
                console.log('点击台词，台词ID:', sourceData.id);
            }}
        >
            {/* 加载状态和错误信息 */}
            {isUpdating && (
                <div className="subtitle-update-status updating">
                    <span className="loading-spinner"></span>
                    <span>正在更新...</span>
                </div>
            )}
            {updateError && (
                <div className="subtitle-update-status error">
                    <span className="error-icon">⚠️</span>
                    <span>{updateError}</span>
                </div>
            )}
            <RoleDropdown
                roles={roles}
                selectedRole={selectedRole}
                onRoleSelect={(roleName, speakerId) => {
                    onRoleSelect(roleName, sourceData.id, speakerId);
                }}
                onNewSpeaker={() => onNewSpeaker(sourceData.id)}
                showNewSpeaker={showNewSpeaker}
                editingRoleKey={editingRoleKey}
                editingRoleName={editingRoleName}
                onStartEditRole={onStartEditRole}
                onSaveRoleEdit={onSaveRoleEdit}
                onCancelRoleEdit={onCancelRoleEdit}
                onEditingRoleNameChange={onEditingRoleNameChange}
                onDeleteRole={onDeleteRole}
                isSeriesSpeaker={isSeriesSpeaker}
                disabled={isTemporarySubtitle || isGenerating}
                onDisabledClick={handleTemporarySubtitleClick}
            />

            <div className={`source-language ${isPureMode ? 'pure-mode' : ''}`}>
                <div className="time-section">
                    <div className="time-box">{sourceData.timeRange.split(' → ')[0]}</div>
                    <div className="time-arrow">→</div>
                    <div className="time-box">{sourceData.timeRange.split(' → ')[1]}</div>
                </div>
                <div
                    className={`dialogue-content ${isPureMode ? 'pure-mode' : ''}`}
                    onMouseEnter={(e) => handleDialogueHover(e, true)}
                    onMouseLeave={(e) => handleDialogueHover(e, false)}
                    style={{ position: 'relative' }}
                >
                    {editingField === 'source' ? (
                        <textarea
                            ref={sourceInputRef}
                            className={`dialogue-input ${isCreating ? 'creating' : ''} ${subtitleProcessingState.isRetranslating && isGlobalRetranslating ? 'translating' : ''}`}
                            value={sourceText}
                            placeholder={isCreating ? "正在创建字幕..." : "请输入原文"}
                            rows={1}
                            spellCheck={false}
                            disabled={isCreating || subtitleProcessingState.isRetranslating || subtitleProcessingState.isRedubbing || isSplitting || isMerging || isGenerating}
                            onChange={(e) => {
                                if (!isCreating) {
                                    setSourceText(e.target.value);
                                    adjustTextareaHeight(sourceInputRef.current);
                                    // 注意：onChange 不更新光标位置，因为输入文字时光标位置会在 onKeyUp/onClick 中更新
                                }
                            }}
                            onInput={() => {
                                if (!isCreating) {
                                    adjustTextareaHeight(sourceInputRef.current);
                                }
                            }}
                            onBlur={() => {
                                if (!isCreating) {
                                    saveEdit('source', sourceText);
                                }
                            }}
                            onKeyDown={(e) => {
                                if (e.key === 'Enter' && !e.shiftKey && !isCreating) {
                                    e.preventDefault();
                                    saveEdit('source', sourceText);
                                }
                            }}
                            onKeyUp={(e) => {
                                // 在编辑状态下，监听键盘移动光标，更新位置（只监听方向键、Home、End等）
                                const cursorMovementKeys = ['ArrowLeft', 'ArrowRight', 'Home', 'End', 'ArrowUp', 'ArrowDown'];
                                if (!isCreating && !isSplitting && !isMerging && cursorMovementKeys.includes(e.key)) {
                                    const input = e.target as HTMLTextAreaElement;
                                    // 使用 setTimeout 确保 selectionStart 已更新
                                    setTimeout(() => {
                                        const cursorPos = input.selectionStart || 0;
                                        // 更新保存的光标位置和拆分位置
                                        updateCursorPositionForInput(cursorPos);
                                    }, 0);
                                }
                            }}
                            onClick={(e) => {
                                e.stopPropagation();
                                // 在编辑状态下，监听鼠标点击，更新位置
                                if (!isCreating && !isSplitting && !isMerging) {
                                    const input = e.target as HTMLTextAreaElement;
                                    // 使用 setTimeout 确保 selectionStart 已更新
                                    setTimeout(() => {
                                        const cursorPos = input.selectionStart || 0;
                                        // 更新保存的光标位置和拆分位置
                                        updateCursorPositionForInput(cursorPos);
                                    }, 0);
                                }
                            }}
                            onFocus={(e) => {
                                // 点击输入框时暂停所有音频
                                if (onAudioPause) {
                                    onAudioPause();
                                }
                                if (!isCreating) {
                                    // 从 ref 获取光标位置（确保获取到最新值）
                                    const savedPosition = cursorPositionRef.current;
                                    // 如果有点击时保存的光标位置，使用该位置；否则跳到末尾
                                    const positionToUse = savedPosition > 0 ? Math.min(savedPosition, e.target.value.length) : e.target.value.length;
                                    e.target.setSelectionRange(positionToUse, positionToUse);
                                    // 使用后重置，避免下次进入编辑时误用
                                    cursorPositionRef.current = 0;
                                    setCursorPosition(0);
                                }
                            }}
                            autoFocus={!isCreating}
                            style={{ 
                                height: inputHeight ? `${inputHeight}px` : 'auto',
                                opacity: isCreating ? 0.6 : 1,
                                cursor: isCreating ? 'not-allowed' : 'text'
                            }}
                        />
                    ) : (
                        <div
                            ref={sourceDialogueTextRef}
                            className={`dialogue-text ${isHighlighted ? 'highlighted-target' : ''} ${subtitleProcessingState.isRetranslating && isGlobalRetranslating ? 'translating' : ''}`}
                            tabIndex={0}
                            onMouseUp={(e) => {
                                // 在拆分或合并或生成中时，禁用文本点击
                                if (isSplitting || isMerging || isGenerating) {
                                    return;
                                }
                                // 在 mouseUp 时处理文本点击，确保 selection 已经建立
                                handleTextClick(e);
                            }}
                            onClick={(e) => {
                                // 在拆分或合并或生成中时，禁用文本点击
                                if (isSplitting || isMerging || isGenerating) {
                                    return;
                                }
                                // onClick 时也更新光标位置（鼠标点击）
                                handleTextClick(e);
                                
                                // 如果当前台词正在处理中（拆分、合并、翻译、配音）或生成中，不显示操作菜单
                                if (subtitleProcessingState.isRetranslating || subtitleProcessingState.isRedubbing || isSplitting || isMerging || isGenerating) {
                                    return;
                                }
                                // 点击原文台词时切换到原视频轨道
                                if (onSwitchTrack) {
                                    onSwitchTrack('original');
                                }
                                // 跳转到对应时间（使用原文台词的时间戳）
                                if (onSeekToTime) {
                                    onSeekToTime(sourceData.timeRange);
                                }
                                startEditing('source');
                            }}
                            onKeyUp={(e) => {
                                // 监听键盘事件，当用户用方向键移动光标时也更新位置
                                if (e.key === 'ArrowLeft' || e.key === 'ArrowRight' || e.key === 'Home' || e.key === 'End') {
                                    if (!isSplitting && !isMerging) {
                                        handleTextClick(e);
                                    }
                                }
                            }}
                            onKeyDown={(e) => {
                                // 在keyDown时也尝试更新，因为某些情况下keyUp可能不触发
                                if (e.key === 'ArrowLeft' || e.key === 'ArrowRight' || e.key === 'Home' || e.key === 'End') {
                                    if (!isSplitting && !isMerging) {
                                        // 延迟一点，确保selection已经更新
                                        setTimeout(() => {
                                            handleTextClick(e);
                                        }, 0);
                                    }
                                }
                            }}
                            style={{ 
                                height: inputHeight ? `${inputHeight}px` : 'auto',
                                opacity: (isSplitting || isMerging) ? 0.5 : 1,
                                pointerEvents: (isSplitting || isMerging) ? 'none' : 'auto',
                                cursor: (isSplitting || isMerging) ? 'not-allowed' : 'pointer'
                            }}
                        >
                            {sourceText || <span className="placeholder-text">请输入原文</span>}
                        </div>
                    )}
                    <div 
                        className={`play-button ${isTemporarySubtitle || isSplitting || isMerging || isGenerating ? 'disabled' : ''}`} 
                        onClick={() => {
                            // 在处理中（拆分、合并）或生成中时禁用播放
                            if (isSplitting || isMerging || isGenerating) {
                                return;
                            }
                            if (isTemporarySubtitle) {
                                handleTemporarySubtitleClick();
                                return;
                            }
                            if (onPlaySubtitle) {
                                onPlaySubtitle(sourceData.timeRange);
                            }
                        }}
                        style={{ 
                            // 移除内联 opacity，让 CSS 控制显示/隐藏（默认隐藏，hover 时显示）
                            opacity: (isSplitting || isMerging) ? 0.5 : undefined,
                            cursor: (isSplitting || isMerging) ? 'not-allowed' : 'pointer',
                            pointerEvents: (isSplitting || isMerging) ? 'none' : 'auto'
                        }}
                    >
                        <PlayCircleOutlined />
                    </div>

                    {/* 源语言悬浮操作条 */}
                    {showSourceActionBar && (
                        <div
                            className="floating-action-bar source-action-bar"
                            style={{
                                position: 'absolute',
                                top: '-35px',
                                left: '0',
                                zIndex: 1000
                            }}
                        >
                            <div className="action-bar-content">
                                {!isTemporarySubtitle && (
                                    <>
                                        <button
                                            className="action-btn"
                                            onClick={() => handleSourceActionBarAction('split-segment')}
                                            disabled={isSplitting || isGenerating}
                                            style={{ opacity: (isSplitting || isGenerating) ? 0.6 : 1 }}
                                        >
                                            <i className="fas fa-cut"></i>
                                            {isSplitting ? '拆分中...' : '拆分台词'}
                                        </button>
                                        <button
                                            className="action-btn"
                                            onClick={() => handleSourceActionBarAction('merge-segment')}
                                            disabled={isMerging || isGenerating}
                                            style={{ opacity: (isMerging || isGenerating) ? 0.6 : 1 }}
                                        >
                                            <i className="fas fa-object-group"></i>
                                            {isMerging ? '合并中...' : '合并台词'}
                                        </button>
                                        <button
                                            className="action-btn"
                                            onClick={() => handleSourceActionBarAction('add-segment')}
                                            disabled={isGenerating}
                                        >
                                            <i className="fas fa-plus"></i>
                                            新增台词
                                        </button>
                                        {/* 纯净版视频编辑页隐藏“重新翻译”按钮 */}
                                        {!isPureMode && (
                                            <button
                                                className="action-btn"
                                                onClick={() => handleRetranslate()}
                                                disabled={isRetranslating || isGenerating}
                                            >
                                                <i className="fas fa-language"></i>
                                                {isRetranslating ? '翻译中...' : '重新翻译'}
                                            </button>
                                        )}
                                    </>
                                )}
                                {/* 纯净版视频编辑页隐藏“添加至音色库 / 移出音色库”按钮 */}
                                {!isTemporarySubtitle && !isPureMode && (
                                    <button
                                        className="action-btn"
                                        onClick={() => {
                                            // 兼容两种数据格式：GetSubtitleResponse 有 speakers，Task.Subtitles 有 speaker
                                            const isSeriesSpeakerValue = isSeriesSpeaker || 
                                                (subtitleData && 'speakers' in subtitleData ? (subtitleData as Subtitles.GetSubtitleResponse).speakers?.is_series_speaker : 
                                                 subtitleData && 'speaker' in subtitleData ? (subtitleData as Task.Subtitles).speaker?.isSeriesSpeaker : false);
                                            handleSourceActionBarAction(
                                                isSeriesSpeakerValue 
                                                    ? 'remove-from-voice-library' 
                                                    : 'add-to-voice-library'
                                            );
                                        }}
                                        disabled={isGenerating}
                                    >
                                        {(() => {
                                            // 兼容两种数据格式：GetSubtitleResponse 有 speakers，Task.Subtitles 有 speaker
                                            const isSeriesSpeakerValue = isSeriesSpeaker || 
                                                (subtitleData && 'speakers' in subtitleData ? (subtitleData as Subtitles.GetSubtitleResponse).speakers?.is_series_speaker : 
                                                 subtitleData && 'speaker' in subtitleData ? (subtitleData as Task.Subtitles).speaker?.isSeriesSpeaker : false);
                                            return (
                                                <>
                                                    <i className={`fas ${isSeriesSpeakerValue ? 'fa-sign-out-alt' : 'fa-save'}`}></i>
                                                    {isSeriesSpeakerValue ? '移出音色库' : '添加至音色库'}
                                                </>
                                            );
                                        })()}
                                    </button>
                                )}
                                {/* 纯净版视频编辑页：左侧悬浮操作条删除台词（无二次确认，直接删除） */}
                                {!isTemporarySubtitle && isPureMode && (
                                    <button
                                        className="action-btn"
                                        disabled={isDeleting || isGenerating}
                                        style={{ opacity: (isDeleting || isGenerating) ? 0.6 : 1 }}
                                        onClick={(e) => {
                                            console.log('🗑️ 纯净版左侧删除按钮被点击，直接调用删除逻辑');
                                            e.preventDefault();
                                            e.stopPropagation();
                                            handleDeleteSubtitle();
                                        }}
                                    >
                                        <i className="fas fa-trash"></i>
                                        {isDeleting ? '删除中...' : '删除'}
                                    </button>
                                )}
                                {isTemporarySubtitle && (
                                    <button
                                        className="action-btn"
                                        disabled={isDeleting || isGenerating}
                                        style={{ opacity: (isDeleting || isGenerating) ? 0.6 : 1 }}
                                        onClick={(e) => {
                                            console.log('🗑️ 左侧临时字幕删除按钮被点击');
                                            e.preventDefault();
                                            e.stopPropagation();
                                            handleDeleteSubtitle();
                                        }}
                                    >
                                        <i className="fas fa-trash"></i>
                                        {isDeleting ? '删除中...' : '删除'}
                                    </button>
                                )}
                            </div>
                        </div>
                    )}
                </div>
            </div>
            {!hideTargetAndComment && (
            <div className="target-language">
                <div className="time-section">
                    {editingTimeField === 'start' ? (
                        <div className="time-input-container">
                            <input
                                ref={startTimeRef}
                                type="text"
                                value={startTime}
                                onChange={(e) => setStartTime(e.target.value)}
                                aria-label="编辑开始时间"
                                onBlur={() => saveTimeEdit('start', startTime)}
                                onKeyDown={(e) => {
                                    if (e.key === 'Enter') {
                                        saveTimeEdit('start', startTime);
                                    } else if (e.key === 'Escape') {
                                        setStartTime(startTime);
                                        setTimeError(null);
                                        setEditingTimeField(null);
                                    }
                                }}
                                className={`time-input editing ${timeError ? 'error' : ''}`}
                                autoFocus
                                disabled
                            />
                            {timeError && editingTimeField === 'start' && (
                                <div className="time-error-tip">{timeError}</div>
                            )}
                        </div>
                    ) : (
                        <div
                            className={`time-box ${isTemporarySubtitle || isSplitting || isMerging || isGenerating ? 'disabled' : ''}`}
                            onClick={() => {
                                // 在处理中（拆分、合并）或生成中时禁用时间跳转
                                if (isSplitting || isMerging || isGenerating) {
                                    return;
                                }
                                if (isTemporarySubtitle) {
                                    handleTemporarySubtitleClick();
                                    return;
                                }
                                // 只跳转视频，不进入编辑模式（使用翻译台词的时间戳）
                                if (onSeekToTime) {
                                    onSeekToTime(targetData.timeRange);
                                }
                            }}
                            style={{ 
                                opacity: (isSplitting || isMerging || isGenerating) ? 0.5 : 1,
                                cursor: (isSplitting || isMerging || isGenerating) ? 'not-allowed' : 'pointer',
                                pointerEvents: (isSplitting || isMerging || isGenerating) ? 'none' : 'auto'
                            }}
                        >
                            {startTime}
                        </div>
                    )}
                    <div className="time-arrow">→</div>
                    {editingTimeField === 'end' ? (
                        <div className="time-input-container">
                            <input
                                ref={endTimeRef}
                                type="text"
                                value={endTime}
                                onChange={(e) => setEndTime(e.target.value)}
                                aria-label="编辑结束时间"
                                onBlur={() => saveTimeEdit('end', endTime)}
                                onKeyDown={(e) => {
                                    if (e.key === 'Enter') {
                                        saveTimeEdit('end', endTime);
                                    } else if (e.key === 'Escape') {
                                        setEndTime(endTime);
                                        setTimeError(null);
                                        setEditingTimeField(null);
                                    }
                                }}
                                className={`time-input editing ${timeError ? 'error' : ''}`}
                                autoFocus
                                disabled
                            />
                            {timeError && editingTimeField === 'end' && (
                                <div className="time-error-tip">{timeError}</div>
                            )}
                        </div>
                    ) : (
                        <div
                            className={`time-box ${isTemporarySubtitle || isSplitting || isMerging || isGenerating ? 'disabled' : ''}`}
                            onClick={() => {
                                // 在处理中（拆分、合并）或生成中时禁用时间跳转
                                if (isSplitting || isMerging || isGenerating) {
                                    return;
                                }
                                if (isTemporarySubtitle) {
                                    handleTemporarySubtitleClick();
                                    return;
                                }
                                // 只跳转视频，不进入编辑模式（使用翻译台词的时间戳）
                                if (onSeekToTime) {
                                    onSeekToTime(targetData.timeRange);
                                }
                            }}
                            style={{ 
                                opacity: (isSplitting || isMerging || isGenerating) ? 0.5 : 1,
                                cursor: (isSplitting || isMerging || isGenerating) ? 'not-allowed' : 'pointer',
                                pointerEvents: (isSplitting || isMerging || isGenerating) ? 'none' : 'auto'
                            }}
                        >
                            {endTime}
                        </div>
                    )}
                </div>
                <div
                    className="dialogue-content"
                    onMouseEnter={(e) => handleDialogueHover(e, true)}
                    onMouseLeave={(e) => handleDialogueHover(e, false)}
                    style={{ position: 'relative' }}
                >
                    {editingField === 'target' ? (
                        <div
                            style={{
                                flex: 1,
                                display: 'flex',
                                flexDirection: 'column',
                                minWidth: 0,
                            }}
                        >
                            {/* 编辑状态下：输入框与右侧按钮同一行显示，按钮始终可见 */}
                            <div
                                style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    minWidth: 0,
                                }}
                            >
                                <div 
                                    className={`dialogue-input-container ${subtitleProcessingState.isRedubbing && isGlobalRedubbing ? 'disabled' : ''}`}
                                    style={{
                                        border: '1px solid #d9d9d9',
                                        borderRadius: '4px',
                                        padding: '0',
                                        backgroundColor: 'white',
                                        minHeight: '36px',
                                        width: '100%',
                                        boxSizing: 'border-box'
                                    }}
                                >
                                    <textarea
                                        ref={targetInputRef}
                                        className={`dialogue-input ${subtitleProcessingState.isRedubbing && isGlobalRedubbing ? 'disabled' : ''}`}
                                        value={targetText}
                                        placeholder="请输入翻译文本"
                                        rows={1}
                                        spellCheck={false}
                                        disabled={subtitleProcessingState.isRetranslating || subtitleProcessingState.isRedubbing || isSplitting || isMerging || isGenerating}
                                        onChange={(e) => {
                                            setTargetText(e.target.value);
                                            adjustTextareaHeight(targetInputRef.current);
                                        }}
                                        onInput={() => adjustTextareaHeight(targetInputRef.current)}
                                        onBlur={() => saveEdit('target', targetText)}
                                        onKeyDown={(e) => {
                                            if (e.key === 'Enter' && !e.shiftKey) {
                                                e.preventDefault();
                                                saveEdit('target', targetText);
                                            }
                                        }}
                                        onClick={(e) => e.stopPropagation()}
                                        onFocus={(e) => {
                                            // 点击输入框时暂停所有音频
                                            if (onAudioPause) {
                                                onAudioPause();
                                            }
                                            const length = e.target.value.length;
                                            e.target.setSelectionRange(length, length);
                                        }}
                                        autoFocus
                                        style={{ 
                                            height: inputHeight ? `${inputHeight}px` : 'auto',
                                            border: 'none',
                                            borderRadius: '4px',
                                            padding: '7px',
                                            resize: 'none',
                                            outline: 'none',
                                            boxShadow: 'none',
                                            width: '100%',
                                            boxSizing: 'border-box',
                                            fontFamily: 'inherit',
                                            fontSize: '14px',
                                            lineHeight: '1.5',
                                            minHeight: '22px'
                                        }}
                                    />
                                </div>
                                <div className="action-buttons">
                                    <div 
                                        className={`play-button ${isTemporarySubtitle || isSplitting || isMerging || isGenerating ? 'disabled' : ''}`} 
                                        onClick={() => {
                                            // 在处理中（拆分、合并）或生成中时禁用播放
                                            if (isSplitting || isMerging || isGenerating) {
                                                return;
                                            }
                                            console.log('目标台词播放按钮被点击', {
                                                isTemporarySubtitle,
                                                hasOnPlayTargetSubtitle: !!onPlayTargetSubtitle,
                                                timeRange: targetData.timeRange,
                                                targetData
                                            });
                                            
                                            if (isTemporarySubtitle) {
                                                handleTemporarySubtitleClick();
                                                return;
                                            }
                                            if (onPlayTargetSubtitle) {
                                                console.log('调用onPlayTargetSubtitle回调函数', {
                                                    timeRange: targetData.timeRange,
                                                    subtitleId: targetData.id
                                                });
                                                onPlayTargetSubtitle(targetData.timeRange, targetData.id);
                                            } else {
                                                console.error('onPlayTargetSubtitle回调函数未定义');
                                            }
                                        }}
                                        style={{ 
                                            opacity: (isSplitting || isMerging || isGenerating) ? 0.5 : 1,
                                            cursor: (isSplitting || isMerging || isGenerating) ? 'not-allowed' : 'pointer',
                                            pointerEvents: (isSplitting || isMerging || isGenerating) ? 'none' : 'auto'
                                        }}
                                    >
                                        <PlayCircleOutlined />
                                    </div>
                                    {shouldShowHistoryIcon && (
                                        <HistoryOutlined
                                            className={(isTemporarySubtitle || isGenerating) ? 'disabled' : ''}
                                            onClick={handleHistoryIconClick}
                                        />
                                    )}
                                </div>
                            </div>
                            {/* 回译内容显示在输入框外部下方，用浅灰色横线隔开（仅回译内容左侧缩进） */}
                            {backTranslateText && (
                                <>
                                    <div
                                        style={{
                                            width: 'calc(100% - 35px)', // 减去 action-buttons 的宽度（约32px）和 margin-left（8px）
                                            height: '1px',
                                            backgroundColor: '#e0e0e0',
                                            marginTop: '8px',
                                            marginBottom: '8px'
                                        }}
                                    />
                                    <div
                                        style={{
                                            padding: '0 0 0 10px',
                                            fontSize: '14px',
                                            color: '#666',
                                            lineHeight: '1.5',
                                            width: 'calc(100% - 35px)', // 减去 action-buttons 的宽度（约32px）和 margin-left（8px）
                                            boxSizing: 'border-box'
                                        }}
                                    >
                                        {backTranslateText}
                                    </div>
                                </>
                            )}
                        </div>
                    ) : (
                        <div
                            style={{
                                flex: 1,
                                display: 'flex',
                                flexDirection: 'column',
                                minWidth: 0,
                            }}
                        >
                            {/* 将 dialogue-text 和 action-buttons 包装在一个容器中，使按钮相对于 dialogue-text 居中 */}
                            <div
                                style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    minWidth: 0,
                                }}
                            >
                                <div
                                    ref={targetDialogueTextRef}
                                    className={`dialogue-text ${isHighlighted ? 'highlighted-target' : ''} ${isTemporarySubtitle ? 'temporary-disabled' : ''} ${subtitleProcessingState.isRedubbing && isGlobalRedubbing ? 'disabled' : ''}`}
                                    onClick={() => {
                                        if (isTemporarySubtitle) {
                                            handleTemporarySubtitleClick();
                                            return;
                                        }
                                        // 如果当前台词正在处理中（拆分、合并、翻译、配音）或生成中，不显示操作菜单
                                        if (subtitleProcessingState.isRetranslating || subtitleProcessingState.isRedubbing || isSplitting || isMerging || isGenerating) {
                                            return;
                                        }
                                        // 点击翻译台词时切换到翻译轨道
                                        if (onSwitchTrack) {
                                            onSwitchTrack('translated');
                                        }
                                        // 跳转到对应时间（使用翻译台词的时间戳）
                                        if (onSeekToTime) {
                                            onSeekToTime(targetData.timeRange);
                                        }
                                        startEditing('target');
                                    }}
                                    style={{ 
                                        flex: 1,
                                        minHeight: inputHeight ? `${inputHeight}px` : 'auto',
                                        opacity: (isSplitting || isMerging || isGenerating) ? 0.5 : 1,
                                        pointerEvents: (isSplitting || isMerging || isGenerating) ? 'none' : 'auto',
                                        cursor: (isSplitting || isMerging || isGenerating) ? 'not-allowed' : 'pointer',
                                        padding: '4px 11px'
                                    }}
                                >
                                    {targetText || <span className="placeholder-text">请输入翻译文本</span>}
                                </div>
                                <div className="action-buttons">
                        <div 
                            className={`play-button ${isTemporarySubtitle || isSplitting || isMerging || isGenerating ? 'disabled' : ''}`} 
                            onClick={() => {
                                // 在处理中（拆分、合并）或生成中时禁用播放
                                if (isSplitting || isMerging || isGenerating) {
                                    return;
                                }
                                console.log('目标台词播放按钮被点击', {
                                    isTemporarySubtitle,
                                    hasOnPlayTargetSubtitle: !!onPlayTargetSubtitle,
                                    timeRange: targetData.timeRange,
                                    targetData
                                });
                                
                                if (isTemporarySubtitle) {
                                    handleTemporarySubtitleClick();
                                    return;
                                }
                                if (onPlayTargetSubtitle) {
                                    console.log('调用onPlayTargetSubtitle回调函数', {
                                        timeRange: targetData.timeRange,
                                        subtitleId: targetData.id
                                    });
                                    onPlayTargetSubtitle(targetData.timeRange, targetData.id);
                                } else {
                                    console.error('onPlayTargetSubtitle回调函数未定义');
                                }
                            }}
                            style={{ 
                                opacity: (isSplitting || isMerging || isGenerating) ? 0.5 : 1,
                                cursor: (isSplitting || isMerging || isGenerating) ? 'not-allowed' : 'pointer',
                                pointerEvents: (isSplitting || isMerging || isGenerating) ? 'none' : 'auto'
                            }}
                        >
                            <PlayCircleOutlined />
                        </div>
                        {shouldShowHistoryIcon && (
                            <HistoryOutlined
                                className={(isTemporarySubtitle || isGenerating) ? 'disabled' : ''}
                                onClick={handleHistoryIconClick}
                            />
                        )}
                                </div>
                            </div>
                            {/* 回译内容显示在输入框外部下方，用浅灰色横线隔开（仅回译内容左侧缩进） */}
                            {backTranslateText && (
                                <>
                                    <div
                                        style={{
                                            width: 'calc(100% - 40px)', // 减去 action-buttons 的宽度（约32px）和 margin-left（8px）
                                            height: '1px',
                                            backgroundColor: '#e0e0e0',
                                            marginTop: '8px',
                                            marginBottom: '8px'
                                        }}
                                    />
                                    <div
                                        style={{
                                            padding: '0 0 0 10px',
                                            fontSize: '14px',
                                            color: '#666',
                                            lineHeight: '1.5',
                                            width: 'calc(100% - 40px)', // 减去 action-buttons 的宽度（约32px）和 margin-left（8px）
                                            boxSizing: 'border-box'
                                        }}
                                    >
                                        {backTranslateText}
                                    </div>
                                </>
                            )}
                        </div>
                    )}
                    {/* 台词历史记录炫酷框 */}
                    {isHistoryPanelOpen && !isTemporarySubtitle && (
                        <div className="subtitle-history-panel" ref={historyPanelRef}>
                            <div className="history-header">生成记录</div>
                            <div className="history-body">
                                {historyLoading && (
                                    <div className="history-loading">加载中...</div>
                                )}
                                {!historyLoading && historyList.length === 0 && (
                                    <div className="history-empty">暂无历史记录</div>
                                )}
                                {!historyLoading && historyList.length > 0 && (() => {
                                    // 找到当前正在应用的记录：只使用后端返回的 selected 字段
                                    let currentAppliedId: number | null = null;
                                    
                                    const selectedItem = historyList.find(item => item.selected);
                                    if (selectedItem) currentAppliedId = selectedItem.id;
                                    
                                    return historyList.map(item => {
                                        // 判断是否为当前正在应用的记录（只匹配第一个）
                                        const isCurrentApplied = currentAppliedId === item.id;
                                        // 判断是否为选中状态（用户手动选中或当前正在应用的记录）
                                        const isActive = activeHistoryId === item.id || isCurrentApplied;
                                        
                                        return (
                                        <div
                                            key={item.id}
                                            className={`history-item ${isActive ? 'active' : ''} ${isRestoring ? 'restoring' : ''}`}
                                        onClick={async (e) => {
                                            // 如果点击的是播放按钮，不触发恢复
                                            if ((e.target as HTMLElement).closest('.history-play-btn')) {
                                                return;
                                            }

                                            if (isRestoring) {
                                                return;
                                            }

                                            // 立即关闭历史面板
                                            if (onCloseHistoryPanel) {
                                                onCloseHistoryPanel();
                                            }
                                            
                                            // 停止音频播放
                                            if (historyAudioRef.current) {
                                                historyAudioRef.current.pause();
                                                historyAudioRef.current.currentTime = 0;
                                            }
                                            setActiveHistoryId(null);

                                            // 后台异步调用恢复接口
                                            (async () => {
                                                try {
                                                    setIsRestoring(true);
                                                    
                                                    const response = await restoreSubtitleHistoryApi(item.id);
                                                    
                                                    if (response.code === 0 && response.data) {
                                                        const restoredData = response.data;
                                                        
                                                        // 更新本地台词文本
                                                        const newPostText = restoredData.post_text || '';
                                                        setTargetText(newPostText);
                                                        
                                                        // 更新 targetData（文本和时间）
                                                        const newTimeRange = restoredData.post_start_time && restoredData.post_end_time
                                                            ? `${restoredData.post_start_time} → ${restoredData.post_end_time}`
                                                            : targetData.timeRange;
                                                        
                                                        onTargetDataChange({
                                                            ...targetData,
                                                            text: newPostText,
                                                            timeRange: newTimeRange
                                                        });
                                                        
                                                        // 更新完整的字幕数据（包括音频、语速、音量等）
                                                        if (onSubtitleUpdate) {
                                                            // 获取当前字幕数据作为原始数据
                                                            const currentSubtitle = propSubtitleData || subtitleData;
                                                            if (currentSubtitle) {
                                                                onSubtitleUpdate(currentSubtitle as Subtitles.GetSubtitleResponse, restoredData);
                                                            }
                                                        }

                                                        // ✅ 本地立即更新 selected 状态：当前点击恢复的记录置为 true，其余为 false
                                                        // 这样无需依赖 postText/transAudio 进行“猜测”，且 UI 立刻正确高亮
                                                        setHistoryList(prev => prev.map(h => ({
                                                            ...h,
                                                            selected: h.id === item.id
                                                        })));
                                                        setActiveHistoryId(item.id);
                                                        
                                                        showSuccess('恢复成功');
                                                        
                                                        // 刷新字幕数据（确保从服务器获取最新数据）
                                                        if (onRefreshSubtitles) {
                                                            await onRefreshSubtitles();
                                                        }
                                                    } else {
                                                        showError(response.msg || '恢复失败，请重试');
                                                    }
                                                } catch (error: any) {
                                                    console.error('恢复历史记录失败:', error);
                                                    const errorMsg = error?.response?.data?.msg || 
                                                                   error?.response?.msg || 
                                                                   error?.msg || 
                                                                   error?.message || 
                                                                   '恢复失败，请重试';
                                                    showError(errorMsg);
                                                } finally {
                                                    setIsRestoring(false);
                                                }
                                            })();
                                        }}
                                    >
                                        {/* 左侧播放按钮 */}
                                        <button
                                            className="history-play-btn"
                                            onClick={async (e) => {
                                                e.stopPropagation(); // 阻止事件冒泡，避免触发恢复
                                                try {
                                                    // 切换选中状态
                                                    setActiveHistoryId(item.id);

                                                    // 停止上一条音频
                                                    if (historyAudioRef.current) {
                                                        historyAudioRef.current.pause();
                                                        historyAudioRef.current.currentTime = 0;
                                                    }

                                                    if (item.transAudio) {
                                                        // 构建完整的音频URL（trans_audio 使用内容哈希命名，可使用强缓存）
                                                        const audioUrl = processTransAudioUrl(item.transAudio);
                                                        
                                                        const audio = new Audio(audioUrl);
                                                        historyAudioRef.current = audio;
                                                        await audio.play();
                                                    }
                                                } catch (error) {
                                                    console.error('播放历史记录音频失败:', error);
                                                }
                                            }}
                                        >
                                            <span className="history-play-triangle"></span>
                                        </button>
                                        {/* 右侧内容区域 */}
                                        <div className="history-content">
                                            <div className="history-text-wrapper">
                                                <div className="history-text">
                                                    {item.postText || '（无文本）'}
                                                </div>
                                                {(activeHistoryId === item.id || isCurrentApplied) && (
                                                    <CheckOutlined className="history-check" />
                                                )}
                                            </div>
                                            <div className="history-meta">
                                                <span className="history-duration">
                                                    {calculateDuration(item.postStartTime, item.postEndTime)}
                                                </span>
                                            </div>
                                        </div>
                                    </div>
                                    );
                                });
                                })()}
                            </div>
                        </div>
                    )}

                    {/* 悬浮操作条 */}
                    {showActionBar && !isTemporarySubtitle && (
                        <div
                            className="floating-action-bar"
                            style={{
                                position: 'absolute',
                                top: '-35px',
                                left: '0',
                                zIndex: 1000
                            }}
                        >
                            <div className="action-bar-content">
                                {!isTemporarySubtitle && !(!targetText || !targetText.trim()) && (
                                    <>
                                        <button
                                            className="action-btn"
                                            onClick={() => handleActionBarAction('change-voice')}
                                            disabled={isGenerating}
                                        >
                                            <i className="fas fa-microphone"></i>
                                            更换音色
                                        </button>
                                        <button
                                            className="action-btn"
                                            onClick={() => handleActionBarAction('adjust-translation')}
                                            disabled={isRetranslating || isGenerating}
                                        >
                                            <i className="fas fa-language"></i>
                                            {isRetranslating ? '翻译中...' : '调整翻译'}
                                        </button>
                                        <button
                                            className="action-btn"
                                            onClick={() => handleActionBarAction('re-dub')}
                                            disabled={isRedubbing || isGenerating}
                                            style={{ opacity: (isRedubbing || isGenerating) ? 0.6 : 1 }}
                                        >
                                            <i className="fas fa-play-circle"></i>
                                            {isRedubbing ? '配音中...' : '重新配音'}
                                        </button>
                                        <button
                                            className="action-btn"
                                            onClick={(e) => handleActionBarAction('adjust-volume', e)}
                                            disabled={isGenerating}
                                        >
                                            <i className="fas fa-volume-up"></i>
                                            调节音量
                                        </button>
                                        {/* <button
                                            className="action-btn"
                                            onClick={(e) => handleActionBarAction('adjust-speed', e)}
                                        >
                                            <i className="fas fa-tachometer-alt"></i>
                                            调节语速
                                        </button> */}
                                    </>
                                )}
                                <Popconfirm
                                    title="确定要删除此句台词吗？"
                                    description="删除后将无法恢复，请谨慎操作。"
                                    onConfirm={() => {
                                        console.log('🎯 Popconfirm 确定按钮被点击');
                                        handleDeleteSubtitle();
                                    }}
                                    onCancel={() => {
                                        console.log('🚫 Popconfirm 取消按钮被点击');
                                    }}
                                    okText="确定"
                                    cancelText="取消"
                                    placement="topRight"
                                    disabled={isDeleting || isGenerating}
                                >
                                    <button
                                        className="action-btn"
                                        disabled={isDeleting || isGenerating}
                                        style={{ opacity: (isDeleting || isGenerating) ? 0.6 : 1 }}
                                        onClick={(e) => {
                                            console.log('🗑️ 删除按钮被点击');
                                            e.preventDefault();
                                            e.stopPropagation();
                                        }}
                                    >
                                        <i className="fas fa-trash"></i>
                                        {isDeleting ? '删除中...' : '删除'}
                                    </button>
                                </Popconfirm>
                            </div>
                        </div>
                    )}

                    {/* 调整翻译下拉菜单 */}
                    {showAdjustDropdown && showActionBar && !isTemporarySubtitle && (
                        <div
                            className="adjust-translation-dropdown"
                            style={{
                                position: 'absolute',
                                top: '2px', // 显示在操作条下方
                                left: '80px', // 调整翻译按钮的位置
                                zIndex: 1001
                            }}
                        >
                            <div className="dropdown-content">
                               
                                <div 
                                    className={`dropdown-item ${isRetranslating ? 'disabled' : ''}`}
                                    onClick={(e) => {
                                        if (isRetranslating) return;
                                        e.stopPropagation();
                                        handleAdjustOption('longer');
                                    }}
                                    style={{ 
                                        opacity: (isRetranslating || isGenerating) ? 0.6 : 1,
                                        cursor: (isRetranslating || isGenerating) ? 'not-allowed' : 'pointer'
                                    }}
                                >
                                    {isRetranslating ? '翻译中...' : '更加长一些'}
                                </div>
                                <div 
                                    className={`dropdown-item ${(isRetranslating || isGenerating) ? 'disabled' : ''}`}
                                    onClick={(e) => {
                                        if (isRetranslating || isGenerating) return;
                                        e.stopPropagation();
                                        handleAdjustOption('shorter');
                                    }}
                                    style={{ 
                                        opacity: isRetranslating ? 0.6 : 1,
                                        cursor: isRetranslating ? 'not-allowed' : 'pointer'
                                    }}
                                >
                                    {isRetranslating ? '翻译中...' : '更加短一些'}
                                </div>
                                <div className="dropdown-item" onClick={(e) => {
                                    e.stopPropagation();
                                    handleAdjustOption('custom');
                                }}>
                                    <i className="fas fa-edit"></i>
                                    自定义
                                    <i className="fas fa-chevron-right"></i>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* 自定义翻译卡片 */}
                    {showCustomCard && showActionBar && showAdjustDropdown && !isTemporarySubtitle && (
                        <div
                            className="custom-translation-card"
                            style={{
                                position: 'absolute',
                                top: '2px', // 与下拉菜单相同位置
                                left: '200px', // 在下拉菜单右侧
                                zIndex: 1001
                            }}
                        >
                            <div className="custom-card-content">
                                <textarea
                                    className="custom-textarea"
                                    placeholder="请输入你的翻译偏好或要求"
                                    value={customText}
                                    maxLength={700}
                                    spellCheck={false}
                                    onChange={(e) => setCustomText(e.target.value)}
                                />
                                <div className="custom-card-footer">
                                    <span className="char-counter">{customText.length} / 700</span>
                                    <button 
                                        className="retranslate-btn"
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            handleCustomRetranslate();
                                        }}
                                        disabled={isRetranslating || !customText || !customText.trim()}
                                    >
                                        {isRetranslating ? '翻译中...' : '重新翻译'}
                                    </button>
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            </div>
            )}

            {/* 留言列 */}
            {!hideTargetAndComment && (
            <div className="comment-column">
                <div className="comment-content">
                    {isEditingComment ? (
                        <div className="comment-edit-container">
                            <textarea
                                className="comment-input"
                                value={localCommentText}
                                onChange={handleCommentChange}
                                onBlur={handleCommentSave}
                                onKeyDown={(e) => {
                                    if (e.key === 'Enter' && !e.shiftKey) {
                                        e.preventDefault();
                                        handleCommentSave();
                                    } else if (e.key === 'Escape') {
                                        setLocalCommentText(commentText);
                                        setIsEditingComment(false);
                                    }
                                }}
                                placeholder="请输入留言..."
                                rows={3}
                                autoFocus
                            />
                        </div>
                    ) : (
                        <div
                            className="comment-display"
                            onClick={handleCommentClick}
                        >
                            {localCommentText ? (
                                <div className="comment-text">
                                    {localCommentText}
                                </div>
                            ) : (
                                <div className="comment-placeholder">
                                    <span>请留言</span>
                                </div>
                            )}
                        </div>
                    )}
                </div>
            </div>
            )}

            {/* 更换音色模态框：挂载到 body 并禁用位移动画，避免先偏右再居中的闪烁 */}
            <Modal
                title={
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <i className="fas fa-microphone" style={{ color: '#4a90e2' }}></i>
                        <span>更换音色</span>
                    </div>
                }
                open={showChangeVoiceModal}
                onCancel={handleCancelChangeVoice}
                footer={[
                    <Button
                        key="confirm"
                        type="primary"
                        onClick={handleConfirmChangeVoice}
                        disabled={changingVoice || isGenerating}
                        loading={changingVoice}
                    >
                        确定
                    </Button>
                ]}
                className="change-voice-modal"
                width={500}
                centered
                getContainer={() => document.body}
                transitionName=""
                maskTransitionName=""
            >
                <div style={{ padding: '8px 0px 0 0' }}>
                    {/* 加载状态 */}
                    {loadingSubtitle && (
                        <div style={{ textAlign: 'center', padding: '20px', color: '#666' }}>
                            正在加载字幕数据...
                        </div>
                    )}

                    {/* 语音条区域 */}
                    {!loadingSubtitle && (
                        <div className="voice-bar-container" style={{ marginBottom: '5px' }}>
                            {/* 剧集音色库标签 */}
                            {(() => {
                                const speaker = getSubtitleField.speaker(subtitleData);
                                const isSeriesSpeaker = speaker && 'is_series_speaker' in speaker ? speaker.is_series_speaker : 
                                                       speaker && 'isSeriesSpeaker' in speaker ? speaker.isSeriesSpeaker : false;
                                const speakerName = speaker && 'speaker_name' in speaker ? speaker.speaker_name :
                                                   speaker && 'speakerName' in speaker ? speaker.speakerName : '';
                                return isSeriesSpeaker ? (
                                    <div style={{
                                        fontSize: '14px',
                                        color: '#666',
                                        marginBottom: '8px',
                                        fontWeight: '500'
                                    }}>
                                        剧集音色库-{speakerName}
                                    </div>
                                ) : null;
                            })()}
                            {/* 主语音条 */}
                            <div className="voice-bar" style={{
                                display: 'flex',
                                alignItems: 'center',
                                background: 'white',
                                border: '2px solid #e0e0e0',
                                borderRadius: '8px',
                                padding: '12px 16px',
                                gap: '12px',
                                transition: 'all 0.2s ease'
                            }}>
                                {/* 左侧播放按钮 */}
                                <div className="play-button" onClick={handlePlayTargetAudio} style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    width: '24px',
                                    height: '24px',
                                    cursor: 'pointer',
                                    transition: 'transform 0.2s ease',
                                    opacity: getSubtitleField.voiceInfo(subtitleData)?.origin_audio ? 1 : 0.5
                                }}>
                                    {isPlayingAudio ? (
                                        // 播放中显示暂停图标
                                        <div style={{
                                            display: 'flex',
                                            gap: '2px',
                                            alignItems: 'center'
                                        }}>
                                            <div style={{
                                                width: '3px',
                                                height: '10px',
                                                backgroundColor: '#000'
                                            }}></div>
                                            <div style={{
                                                width: '3px',
                                                height: '10px',
                                                backgroundColor: '#000'
                                            }}></div>
                                        </div>
                                    ) : (
                                        // 未播放显示播放三角形
                                        <div className="play-triangle" style={{
                                            width: '0',
                                            height: '0',
                                            borderLeft: '8px solid #000',
                                            borderTop: '5px solid transparent',
                                            borderBottom: '5px solid transparent'
                                        }}></div>
                                    )}
                                </div>

                                {/* 中间内容区域 */}
                                <div className="voice-content" style={{
                                    flex: 1,
                                    display: 'flex',
                                    flexDirection: 'column',
                                    gap: '4px'
                                }}>
                                    <div className="voice-text" style={{
                                        fontSize: '14px',
                                        color: '#333',
                                        fontWeight: '800'
                                    }}>{getSubtitleField.originText(subtitleData) || ''}</div>
                                    <div className="voice-timestamp" style={{
                                        fontSize: '9px',
                                        color: '#666'
                                    }}>{subtitleData ? `${getSubtitleField.originStartTime(subtitleData) || ''} - ${getSubtitleField.originEndTime(subtitleData) || ''}` : ''}</div>
                                </div>

                                {/* 右侧下拉选择器 */}
                                <div className="voice-selector" onClick={handleToggleSegmentList} style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '8px',
                                    cursor: 'pointer',
                                    padding: '8px 12px',
                                    borderRadius: '6px',
                                    transition: 'background-color 0.2s ease'
                                }}>
                                    <span className="selector-text" style={{
                                        fontSize: '12px',
                                        color: '#333'
                                    }}>选择其他片段模拟</span>
                                    <i className={`fas fa-chevron-${showSegmentList ? 'up' : 'down'} selector-arrow`} style={{
                                        fontSize: '12px',
                                        color: '#666',
                                        transition: 'transform 0.2s ease'
                                    }}></i>
                                </div>
                            </div>

                            {/* 其他片段列表 */}
                            {showSegmentList && (
                                <div className="segment-list" style={{
                                    border: '1px solid #e0e0e0',
                                    borderRadius: '8px',
                                    background: 'white',
                                    overflow: 'hidden'
                                }}>
                                    <div className="segment-list-title" style={{
                                        padding: '12px 16px',
                                        background: '#f8f9fa',
                                        fontSize: '14px',
                                        color: '#333',
                                        fontWeight: '500',
                                        borderBottom: '1px solid #e0e0e0'
                                    }}>选择该说话人其他片段</div>
                                    <div className="segment-items" style={{
                                        maxHeight: '190px',
                                        overflowY: 'auto'
                                    }}>
                                        {loadingSpeakerSubtitles ? (
                                            <div style={{ textAlign: 'center', padding: '20px', color: '#666' }}>
                                                正在加载其他片段...
                                            </div>
                                        ) : (
                                            speakerSubtitles.map((subtitle, index) => (
                                                <div
                                                    key={subtitle.id}
                                                    className={`segment-item ${index === 0 ? 'active' : ''}`}
                                                    style={{
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        padding: '12px 16px',
                                                        gap: '12px',
                                                        borderBottom: '1px solid #f0f0f0',
                                                        transition: 'background-color 0.2s ease',
                                                        backgroundColor: selectedSegment?.id === subtitle.id ? '#e6f7ff' : (index === 0 ? '#f5f5f5' : 'transparent'),
                                                        cursor: changingVoice ? 'not-allowed' : 'pointer',
                                                        opacity: changingVoice ? 0.6 : 1
                                                    }}
                                                    onClick={() => !changingVoice && handleChangeVoice(subtitle)}
                                                >
                                                    <div className="play-button" onClick={(e) => {
                                                        e.stopPropagation();
                                                        handlePlaySegmentAudio(subtitle);
                                                    }} style={{
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        justifyContent: 'center',
                                                        width: '20px',
                                                        height: '20px',
                                                        cursor: 'pointer',
                                                        transition: 'transform 0.2s ease',
                                                        opacity: subtitle.origin_audio ? 1 : 0.5
                                                    }}>
                                                        {playingSegmentId === subtitle.id ? (
                                                            // 播放中显示暂停图标
                                                            <div style={{
                                                                display: 'flex',
                                                                gap: '1px',
                                                                alignItems: 'center'
                                                            }}>
                                                                <div style={{
                                                                    width: '2px',
                                                                    height: '8px',
                                                                    backgroundColor: '#000'
                                                                }}></div>
                                                                <div style={{
                                                                    width: '2px',
                                                                    height: '8px',
                                                                    backgroundColor: '#000'
                                                                }}></div>
                                                            </div>
                                                        ) : (
                                                            // 未播放显示播放三角形
                                                            <div className="play-triangle" style={{
                                                                width: '0',
                                                                height: '0',
                                                                borderLeft: '6px solid #000',
                                                                borderTop: '4px solid transparent',
                                                                borderBottom: '4px solid transparent'
                                                            }}></div>
                                                        )}
                                                    </div>
                                                    <div className="segment-content" style={{
                                                        flex: 1,
                                                        display: 'flex',
                                                        flexDirection: 'column',
                                                        gap: '2px'
                                                    }}>
                                                        <div className="segment-text" style={{
                                                            fontSize: '13px',
                                                            color: '#333'
                                                        }}>{subtitle.origin_text}</div>
                                                        <div className="segment-timestamp" style={{
                                                            fontSize: '11px',
                                                            color: '#666'
                                                        }}>{subtitle.origin_start_time} - {subtitle.origin_end_time}</div>
                                                    </div>
                                                    <div style={{
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        gap: '6px'
                                                    }}>
                                                        {index === 0 && (
                                                            <div style={{
                                                                fontSize: '10px',
                                                                color: '#fff',
                                                                backgroundColor: '#ff6b35',
                                                                padding: '2px 6px',
                                                                borderRadius: '3px',
                                                                fontWeight: '500'
                                                            }}>推荐</div>
                                                        )}
                                                        {selectedSegment?.id === subtitle.id && (
                                                            <div className="segment-check" style={{
                                                                display: 'flex',
                                                                alignItems: 'center',
                                                                justifyContent: 'center',
                                                                width: '20px',
                                                                height: '20px',
                                                                color: '#4a90e2'
                                                            }}>
                                                                <i className="fas fa-check" style={{ fontSize: '12px' }}></i>
                                                            </div>
                                                        )}
                                                    </div>
                                                </div>
                                            ))
                                        )}
                                    </div>
                                </div>
                            )}
                        </div>
                    )}

                    {/* 提示信息 */}
                    <div style={{ fontSize: '11px', color: '#666', marginBottom: '20px' }}>
                        <span
                            style={{ color: '#4a90e2', cursor: 'pointer', marginLeft: '4px' }}
                            onClick={handleVoiceLibraryClick}
                        >
                            使用剧集音色库
                        </span>
                    </div>

                    {/* 应用到所有片段选项 */}
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <Checkbox
                            checked={applyToAllSegments}
                            onChange={(e) => setApplyToAllSegments(e.target.checked)}
                        />
                        <span style={{ fontSize: '11px', color: '#333' }}>
                            将此更改应用于所有"{selectedRole}"的片段
                        </span>
                    </div>
                </div>
            </Modal>

            {/* 剧集音色库模态框 */}
            <Modal
                title={
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <i className="fas fa-music" style={{ color: '#4a90e2' }}></i>
                        <span>剧集音色库</span>
                    </div>
                }
                open={showVoiceLibraryModal}
                onCancel={handleVoiceLibraryCancel}
                footer={null}
                className="voice-library-modal"
                width={500}
                centered
                destroyOnClose={true}
                maskClosable={false}
                transitionName=""
                style={{
                    position: 'fixed',
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%, -50%)',
                    margin: 0
                }}
                styles={{
                    content: {
                        height: '550px',
                        maxHeight: '550px',
                        display: 'flex',
                        flexDirection: 'column',
                        padding: 0
                    },
                    body: {
                        flex: 1,
                        padding: '20px 24px',
                        overflowY: 'auto',
                        overflowX: 'hidden',
                        scrollbarWidth: 'none', // Firefox 隐藏滚动条
                        msOverflowStyle: 'none', // IE 和 Edge 隐藏滚动条
                        minHeight: 0
                    },
                    header: {
                        flexShrink: 0,
                        padding: '16px 24px',
                        borderBottom: '1px solid #f0f0f0'
                    }
                }}
            >
                <div>
                    {/* 加载状态 */}
                    {loadingSeriesSpeakers && (
                        <div style={{ textAlign: 'center', padding: '20px', color: '#666' }}>
                            正在加载剧集音色库...
                        </div>
                    )}

                    {/* 动态渲染说话人角色 */}
                    {!loadingSeriesSpeakers && seriesSpeakers.map((speaker) => (
                        <div key={speaker.id} style={{
                            marginBottom: '20px',
                            border: '1px solid #e0e0e0',
                            borderRadius: '8px',
                            backgroundColor: '#fafafa'
                        }}>
                            <div
                                style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '8px',
                                    cursor: 'pointer',
                                    padding: '12px 16px',
                                    borderBottom: expandedRoles[speaker.speaker_name] ? '1px solid #e0e0e0' : 'none',
                                    borderRadius: expandedRoles[speaker.speaker_name] ? '8px 8px 0 0' : '8px'
                                }}
                                onClick={() => toggleRoleExpansion(speaker.speaker_name)}
                            >
                                {expandedRoles[speaker.speaker_name] ? <DownOutlined /> : <RightOutlined />}
                                <span style={{ fontSize: '16px', fontWeight: '600', color: '#333' }}>
                                    {speaker.speaker_name}（{speaker.subtitles ? speaker.subtitles.length : 0}）
                                </span>
                            </div>

                            {expandedRoles[speaker.speaker_name] && (
                                <div style={{ padding: '12px 16px 16px 16px' }}>
                                    {speaker.subtitles && speaker.subtitles.length > 0 ? (
                                        speaker.subtitles.map((subtitle, index) => (
                                            <div key={index} style={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'space-between',
                                                padding: '12px 16px',
                                                marginBottom: '8px',
                                                border: '1px solid #e0e0e0',
                                                borderRadius: '4px',
                                                backgroundColor: '#fff'
                                            }}>
                                                <span style={{ fontSize: '14px', color: '#333' }}>{subtitle.origin_text}</span>
                                                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                                                    <div
                                                        style={{
                                                            width: '0',
                                                            height: '0',
                                                            borderLeft: playingVoiceClipId === `${subtitle.subtitle_id}` ? '0' : '6px solid #000',
                                                            borderRight: playingVoiceClipId === `${subtitle.subtitle_id}` ? '0' : 'none',
                                                            borderTop: playingVoiceClipId === `${subtitle.subtitle_id}` ? '6px solid #000' : '4px solid transparent',
                                                            borderBottom: playingVoiceClipId === `${subtitle.subtitle_id}` ? '6px solid #000' : '4px solid transparent',
                                                            cursor: 'pointer',
                                                            transition: 'all 0.2s ease'
                                                        }}
                                                        onClick={() => handlePlayVoiceClip(subtitle.origin_text, subtitle.origin_audio, subtitle.subtitle_id)}
                                                    ></div>
                                                    <Button
                                                        size="small"
                                                        style={{

                                                            backgroundColor: '#4d83d5',
                                                            color: '#fff',
                                                            fontSize: '12px',
                                                            height: '24px',
                                                            padding: '0 8px',
                                                            lineHeight: "20px"
                                                        }}
                                                        onClick={() => handleUseVoiceClip(subtitle)}
                                                    >
                                                        使用
                                                    </Button>
                                                </div>
                                            </div>
                                        ))
                                    ) : (
                                        <div style={{ fontSize: '14px', color: '#666', textAlign: 'center', padding: '20px 0' }}>
                                            暂无音色片段
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>
                    ))}

                    {/* 无数据状态 */}
                    {!loadingSeriesSpeakers && seriesSpeakers.length === 0 && (
                        <div style={{ fontSize: '14px', color: '#666', textAlign: 'center', padding: '40px 0' }}>
                            暂无剧集音色库数据
                        </div>
                    )}
                </div>
            </Modal>

            {/* 添加至剧集音色库模态框 */}
            <Modal
                title="添加至剧集音色库"
                open={showAddToVoiceLibraryModal}
                onCancel={handleAddToVoiceLibraryCancel}
                footer={[
                    <Button key="cancel" onClick={handleAddToVoiceLibraryCancel}>
                        取消
                    </Button>,
                    <Button
                        key="confirm"
                        type="primary"
                        onClick={handleAddToVoiceLibraryConfirm}
                        loading={isAddingToVoiceLibrary}
                        disabled={!selectedRoleForVoiceLibrary || isAddingToVoiceLibrary}
                    >
                        确定
                    </Button>
                ]}
                className="add-to-voice-library-modal"
                width={400}
                centered
                destroyOnClose={true}
                maskClosable={false}
                transitionName=""
                style={{
                    height: '420px',
                    position: 'fixed',
                    top: '40%',
                    left: '50%',
                    transform: 'translate(-50%, -50%)',
                    margin: 0
                }}
                bodyStyle={{
                    height: '330px',
                    padding: '0px 0',
                    marginTop: '10px',
                    overflowY: 'auto',
                    scrollbarWidth: 'thin',
                    scrollbarColor: '#c1c1c1 transparent'
                }}
            >
                <div
                    style={{
                        height: '100%',
                        overflowY: 'auto',
                        scrollbarWidth: 'thin',
                        scrollbarColor: '#c1c1c1 transparent'
                    }}
                    className="add-to-voice-library-scroll"
                >
                    {/* 新建角色按钮和输入框 */}
                    <div style={{
                        display: 'flex',
                        transition: 'all 0.3s ease',
                        alignItems: 'center',
                        width: '100%'
                    }}>
                        
                        <Input
                            placeholder="请输入角色名"
                            value={newRoleName}
                            onChange={(e) => setNewRoleName(e.target.value)}
                            onPressEnter={handleConfirmCreateRole}
                            autoFocus={isCreatingRole}
                            style={{
                                flex: isCreatingRole ? 1 : 0,
                                width: isCreatingRole ? 'auto' : 0,
                                opacity: isCreatingRole ? 1 : 0,
                                overflow: 'hidden',
                                transition: 'all 0.3s ease',
                                margin: 0,
                                marginRight: isCreatingRole ? '5px' : 0,
                                padding: isCreatingRole ? undefined : 0,
                                border: isCreatingRole ? undefined : 'none',
                                visibility: isCreatingRole ? 'visible' : 'hidden',
                                height: '40px',
                                borderRadius: 0
                            }}
                            className="new-role-input"
                        />
                        <Button
                            type="primary"
                            icon={isCreatingRole ? <i className="fas fa-check"></i> : <i className="fas fa-plus" style={{ marginRight: '8px' }}></i>}
                            onClick={isCreatingRole ? handleConfirmCreateRole : handleStartCreateRole}
                            style={{
                                height: '40px',
                                minWidth: isCreatingRole ? '40px' : 'auto',
                                width: isCreatingRole ? '40px' : '100%',
                                transition: 'all 0.3s ease',
                                flexShrink: 0,
                                marginLeft: 'auto',
                                borderRadius: '5px'
                            }}
                            className={`add-to-voice-library-create-role-btn ${isCreatingRole ? "confirm-create-role-btn" : "create-role-btn"}`}
                        >
                            {!isCreatingRole && '新建角色'}
                        </Button>
                    </div>

                    {/* 角色选择列表 */}
                    <div className="role-select-list" style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                        {loadingSeriesSpeakers ? (
                            <div style={{
                                textAlign: 'center',
                                padding: '20px',
                                color: '#666',
                                fontSize: '14px'
                            }}>
                                正在加载剧集说话人角色...
                            </div>
                        ) : localSeriesSpeakers.length > 0 ? (
                            localSeriesSpeakers.map((speaker) => (
                                <div
                                    key={speaker.key}
                                    style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '12px',
                                        justifyContent: 'space-between',
                                        padding: '2px 16px',
                                        border: selectedRoleForVoiceLibrary === speaker.label ? '1px solid #4a90e2' : '1px solid #e0e0e0',
                                        borderRadius: '4px',
                                        backgroundColor: selectedRoleForVoiceLibrary === speaker.label ? '#f0f8ff' : '#fff',
                                        cursor: 'pointer',
                                        transition: 'all 0.2s ease',
                                        minHeight: '44px',
                                        flexShrink: 0
                                    }}
                                    onClick={() => handleRoleSelectForVoiceLibrary(speaker.label)}
                                >
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                                        <i className="fas fa-music" style={{ color: '#4a90e2', fontSize: '16px' }}></i>
                                        <span style={{ fontSize: '14px', color: '#333', fontWeight: '500' }}>{speaker.label}</span>
                                    </div>

                                    {!!speaker.id && (
                                        <img
                                            alt="删除"
                                            src="/img/del.png"
                                            className="add-to-voice-library-remove-role-btn"
                                            style={{
                                                width: '27px',
                                                height: '27px',
                                                cursor: deletingRoleId === speaker.id ? 'not-allowed' : 'pointer',
                                                padding: '4px',
                                                opacity: deletingRoleId === speaker.id ? 0.5 : 1
                                            }}
                                            onClick={(e) => {
                                                e.preventDefault();
                                                e.stopPropagation();
                                                if (deletingRoleId) return;
                                                handleRemoveSeriesSpeakerRole(speaker.id as number);
                                            }}
                                            onKeyDown={(e: any) => {
                                                if (e.key === 'Enter' || e.key === ' ') {
                                                    e.preventDefault();
                                                    e.stopPropagation();
                                                    if (deletingRoleId) return;
                                                    handleRemoveSeriesSpeakerRole(speaker.id as number);
                                                }
                                            }}
                                            tabIndex={0}
                                            aria-label={`移出角色 ${speaker.label}`}
                                        />
                                    )}
                                </div>
                            ))
                        ) : (
                            <div style={{
                                textAlign: 'center',
                                padding: '20px',
                                color: '#666',
                                fontSize: '14px'
                            }}>
                                暂无剧集说话人角色
                            </div>
                        )}
                    </div>
                </div>
            </Modal>


            {/* 音量调节弹出框 */}
            <VolumeAdjustmentPopup
                visible={showVolumeAdjustment}
                onClose={() => setShowVolumeAdjustment(false)}
                onConfirm={handleVolumeAdjustmentConfirm}
                initialVolume={currentVolume ?? getSubtitleField.volumeGainDb(subtitleData)}
                position={volumeAdjustmentPosition}
                loading={loadingVolumeData}
            />

            {/* 倍速调节弹出框 */}
            <SpeedAdjustmentPopup
                visible={showSpeedAdjustment}
                onClose={() => setShowSpeedAdjustment(false)}
                onSpeedChange={handleSpeedAdjustmentConfirm}
                initialSpeed={getSubtitleField.audioSpeed(subtitleData) ?? 1.0}
                position={speedAdjustmentPosition}
                resetTrigger={speedResetTrigger}
            />

            {/* AlertMessage组件 */}
            <AlertMessage config={alertConfig} onClose={hideAlert} />

        </div>
    );
};

export default RowItem;
export { };
